const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

function formatWithDelimiters(dollars, precision, thousands, decimal) {
  const negative = dollars < 0;
  const [whole, fraction = ""] = Math.abs(dollars).toFixed(precision).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  const body = precision > 0 ? `${grouped}${decimal}${fraction}` : grouped;
  return negative ? `-${body}` : body;
}

function stripZeroFraction(value, decimal) {
  const escaped = decimal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return value.replace(new RegExp(`${escaped}00$`), "");
}

function applyThemeMoneyFormat(format, dollars, isPresentment) {
  const rounded = Math.round(dollars);
  const amount = isPresentment
    ? String(rounded)
    : dollars.toFixed(2).replace(/\.00$/, "");

  const separated = (precision, thousands, decimal) => {
    const prec = isPresentment ? 0 : precision;
    const formatted = formatWithDelimiters(
      prec === 0 ? rounded : dollars,
      prec,
      thousands,
      decimal
    );
    return prec === 0 ? formatted : stripZeroFraction(formatted, decimal);
  };

  const tokenValue = (token) => {
    switch (token) {
      case "amount":
        return amount;
      case "amount_no_decimals":
        return String(rounded);
      case "amount_with_comma_separator":
        return separated(2, ".", ",");
      case "amount_no_decimals_with_comma_separator":
        return separated(0, ".", ",");
      case "amount_with_apostrophe_separator":
        return separated(2, "'", ".");
      case "amount_no_decimals_with_space_separator":
        return separated(0, " ", ".");
      case "amount_with_space_separator":
        return separated(2, " ", ",");
      case "amount_with_period_and_space_separator":
        return separated(2, " ", ".");
      default:
        return null;
    }
  };

  return String(format || "").replace(MONEY_PLACEHOLDER, (match, token) => {
    const value = tokenValue(token);
    return value == null ? match : value;
  });
}

function formatMoneyWithTheme(dollars) {
  const moneyFormat =
    window.themeVariables?.settings?.moneyFormat || "${{amount}}";
  const moneyWithCurrencyFormat =
    window.themeVariables?.settings?.moneyWithCurrencyFormat ||
    "${{amount}} USD";
  const currencyCodeEnabled =
    window.themeVariables?.settings?.currencyCodeEnabled || false;
  const numeric = Number(dollars);
  if (!Number.isFinite(numeric)) return "";
  const shopCurrency = window.themeVariables?.settings?.shopCurrency || "USD";
  const activeCurrency = window.Shopify?.currency?.active || shopCurrency;
  const isPresentment =
    activeCurrency !== shopCurrency ||
    Number(window.Shopify?.currency?.rate) > 1;
  const format = currencyCodeEnabled ? moneyWithCurrencyFormat : moneyFormat;
  return applyThemeMoneyFormat(format, numeric, isPresentment)
    .replace(/^ƒ\s?/, "Afl ")
    .replace(/^([A-Za-z.]+)(?=\d)/, "$1 ");
}

// `productGroupingInjected` fires on every cart drawer re-render, and each
// quick-view open constructs another ProductGrouping, so the size guide used to
// be refetched once per live instance per event. Share the parsed content —
// and any in-flight request — across instances, keyed by page handle.
const sizeGuideRequests = new Map();

function fetchSizeGuideContent(handle) {
  if (sizeGuideRequests.has(handle)) {
    return sizeGuideRequests.get(handle);
  }

  // `?view=size-guide-ajax` renders `page.content` under `{% layout none %}`.
  // The full-page shape is still handled below, in case that view isn't
  // deployed yet: a themed page carries the overlay group's
  // `<cart-drawer id="cart-drawer">`, and assigning that to a connected node
  // upgrades the duplicate drawer so one cart-icon click then opens both.
  const request = fetch(`/pages/${handle}?view=size-guide-ajax`)
    .then(response => response.text())
    .then(html => {
      // A DOMParser document has no custom element registry, so nothing in
      // here upgrades while we pick the content apart.
      const doc = new DOMParser().parseFromString(html, "text/html");
      const isFullPage = doc.querySelector(".shopify-section, cart-drawer");
      const content = isFullPage
        ? doc.querySelector(".shopify-section--main-page .prose") ||
          doc.querySelector(".shopify-section--main-page .page")
        : doc.body;
      if (!content) {
        console.warn("[ProductGrouping] No page content in size guide:", handle);
        return null;
      }
      return content.innerHTML;
    })
    .catch(error => {
      sizeGuideRequests.delete(handle);
      throw error;
    });

  sizeGuideRequests.set(handle, request);
  return request;
}

class ProductGrouping extends HTMLElement {
  /** Session key: JSON map family → { dim, value, prefix } for swatch product-handle overrides */
  static SUBOPT_PREFS_KEY = "faherty_pg_subopt_prefs";
  /** Option choices handed from a swapped-out instance to the one that replaces it (see _handleOptionChange) */
  static pendingCarryOver = null;

  constructor() {
    super();
    this.debug = false;
    this.parentComponent = this;
    this.sectionId = this.getAttribute("data-section-id");
    this.optionsToRequestData = this.querySelectorAll(
      "[js-request-new-product]"
    );
    this.buttonsToUpdateSubOptions = this.querySelectorAll(
      "[js-update-sub-option]"
    );
    this.buttonsToUpdateVariant = this.querySelectorAll("[js-update-variant]");

    this.defaultVariantId = this.getAttribute("data-default-variant-id");
    this.currentProductId = this.getAttribute("data-current-product-id");
    // The PDP loaded without a preselected variant: no variant until the shopper has chosen every option
    this.selectionPending = this.dataset.selectionPending === "true";
    this.variantMatrix = JSON.parse(
      this.querySelector("script[data-variant-matrix]")?.textContent || "[]"
    );
    this.currentVariantId = this.selectionPending
      ? null
      : this._getVariantFromURL() || this.defaultVariantId;
    this.currentVariantAvailable =
      this.getAttribute("data-default-variant-available") === "true";
    this.currentProductHandle = this.getAttribute(
      "data-current-product-handle"
    );
    this.currentOptions = {
      option2: this.getAttribute("data-current-option2"),
      option3: this.getAttribute("data-current-option3"),
    };

    // Track user's preferred size choice throughout the PDP session
    this.preferredSize = null;
    // If variant is in URL, treat it as user-selected
    const urlParams = new URLSearchParams(window.location.search);
    this.hasUserSelectedSize = !this.selectionPending && urlParams.has("variant");

    // Track current variant availability for AMP modal close protection
    this.currentRealTimeAvailable = null;

    // Performance: Cache product data to avoid redundant fetches
    this.productCache = new Map();
    this.htmlCache = new Map();
    this.productRequestId = 0;
    this.productRequestController = null;

    this.isQuickView =
      this.dataset.isQuickView === "true" ||
      Boolean(this.closest("quick-view-modal"));

    if (this.isQuickView) {
      if (this.debug) console.log("ProductGrouping: quick-view mode enabled");
    }

    this.productGroupingData = {};
    this.dataProductGroup = JSON.parse(
      this.querySelector("script[data-product-group]")?.textContent || "{}"
    );

    // Prevent custom.js from interfering with our button state management
    this._preventVariantChangeInterference();

    // Choices carried over from the product the shopper swapped away from (Fit, Pocket, Color)
    const carryOver = ProductGrouping.pendingCarryOver;
    ProductGrouping.pendingCarryOver = null;
    if (this.selectionPending) {
      this._initPendingSelection(carryOver);
    }

    this._checkAndSetDefaultVariant();
    this._bindEvents();
    this._onProductGroupingInjectedForSizeGuide = () => this._updateSizeGuide();

    // CRITICAL: Set up button state guardian to prevent buttons from disappearing
    this._setupButtonStateGuardian();
  }

  // Registered here rather than in the constructor so it can be released:
  // quick view replaces its content wholesale, and a window listener held by a
  // detached instance keeps that instance — and its work — alive forever.
  connectedCallback() {
    window.addEventListener("productGroupingInjected", this._onProductGroupingInjectedForSizeGuide);
  }

  disconnectedCallback() {
    window.removeEventListener("productGroupingInjected", this._onProductGroupingInjectedForSizeGuide);
  }

  _preventVariantChangeInterference() {
    // NOTE: We should NOT block variant:change events - other systems need them
    // The ProductGrouping component will dispatch its own variant:change events
    // and manage button state directly, ensuring consistency
  }

  _bindEvents() {
    this.optionsToRequestData.forEach((option) => {
      option.addEventListener("click", (event) =>
        this._handleOptionChange(event, option)
      );
    });
    this._bindVariantButtons();
  } 

  _bindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      // Store the bound handler for later removal
      const handler = (event) => this._handleUpdateVariant(event, button);
      button._variantClickHandler = handler;
      button.addEventListener('click', handler);
    });
  }

  _rebindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      if (button._variantClickHandler) {
        button.removeEventListener('click', button._variantClickHandler);
        button._variantClickHandler = null;
      }
    });
    this.buttonsToUpdateVariant = this.querySelectorAll("[js-update-variant]");
    this._bindVariantButtons();
    this._syncSizeOptionAvailability();
  }

  /** Submit add-to-cart inside <buy-buttons>, not BIS / Apple Pay. */
  _buyButtonsSubmitButton(allButtons) {
    const list = Array.isArray(allButtons)
      ? allButtons
      : Array.from(allButtons || []);
    return (
      list.find(
        (b) =>
          !b.hasAttribute("data-bis-trigger") &&
          b.type === "submit" &&
          !b.classList.contains("custom-apple-pay-btn")
      ) ||
      list.find(
        (b) =>
          !b.hasAttribute("data-bis-trigger") &&
          !b.classList.contains("custom-apple-pay-btn")
      ) ||
      null
    );
  }

  _checkAndSetDefaultVariant() {
    if (!this.currentVariantId && this.defaultVariantId) {
      this._updateURLWithVariant(this.defaultVariantId);
      this.currentVariantId = this.defaultVariantId;
    }

    const scriptTagsData = this.querySelectorAll(
      "script[js-product-group-data]"
    );
    this.productGroupingData = Array.from(scriptTagsData).reduce(
      (acc, script) => {
        const data = JSON.parse(script.textContent || "{}");
        const key = script.getAttribute("data-product-id");
        if (!acc[key]) {
          acc[key] = { tag_options: [], variants: [] };
        }
        const existingData = acc[key];
        existingData.tag_options.push(...data.tag_options);
        const uniqueVariants = data.variants.filter(
          (newVariant) =>
            !existingData.variants.some(
              (existingVariant) => existingVariant.id === newVariant.id
            )
        );
        existingData.variants.push(...uniqueVariants);
        return acc;
      },
      {}
    );

    this._syncSizeOptionAvailability();

    // Check real-time availability for the current variant on page load
    if (this.currentVariantId) {
      const initialAvailabilityVariantId = this.currentVariantId;
      this._fetchVariantAvailability(initialAvailabilityVariantId)
        .then((realTimeAvailable) => {
          if (String(this.currentVariantId) !== String(initialAvailabilityVariantId)) {
            return;
          }
          const availableForUi =
            realTimeAvailable == null
              ? this.currentVariantAvailable
              : realTimeAvailable;

          const addToCartForm = document.querySelector(
            ".product-info .shopify-product-form"
          );
          const addToCartButton = addToCartForm?.querySelector(
            'buy-buttons button[type="submit"]'
          );
          const notifyMeBtn = addToCartForm?.querySelector(
            "buy-buttons button[data-bis-trigger]"
          );
          const addToCartButtonText = addToCartButton?.querySelector("div");
          const soldOutElement = document.querySelector(
            ".product-info sold-out-badge"
          );

          if (addToCartButton && notifyMeBtn && addToCartButtonText) {
            if (!availableForUi) {
              // If user hasn't selected a size, try to find first available
              if (!this.hasUserSelectedSize) {
                this._findFirstAvailableVariantAsync().then(
                  (firstAvailableVariant) => {
                    if (firstAvailableVariant) {
                      this._switchToVariant(firstAvailableVariant);
                    } else {
                      this._updateButtonVisibility(
                        addToCartButton,
                        notifyMeBtn,
                        addToCartButtonText,
                        soldOutElement,
                        false
                      );
                      this._setupWaitlistScroll();
                    }
                  }
                );
                return;
              }

              // User selected a specific unavailable size
              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                addToCartButtonText,
                soldOutElement,
                false
              );
              this._setupWaitlistScroll();
            } else if (availableForUi) {
              // Variant is available - ensure button is visible
              let currentVariant = this._getCurrentVariant?.();
              
              // If variant doesn't have price, try to get it from multiple sources
              if (currentVariant && (!currentVariant.price || currentVariant.price === null || currentVariant.price === undefined)) {
                // First, try to get price from variant picker script (most reliable)
                const variantScript = document.querySelector('variant-picker script[data-variant]');
                if (variantScript) {
                  try {
                    const variantFromScript = JSON.parse(variantScript.textContent || '{}');
                    if (variantFromScript && variantFromScript.id && variantFromScript.id.toString() === this.currentVariantId.toString() && variantFromScript.price) {
                      currentVariant.price = variantFromScript.price;
                      if (this.debug) {
                        console.log('[ProductGrouping] Got price from variant picker script:', currentVariant.price);
                      }
                    }
                  } catch (e) {
                    if (this.debug) {
                      console.warn('[ProductGrouping] Could not parse variant script:', e);
                    }
                  }
                }
                
                // If still no price, try to get from active button
                if ((!currentVariant.price || currentVariant.price === null || currentVariant.price === undefined)) {
                  const activeButton = document.querySelector('button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]');
                  if (activeButton && activeButton.dataset.variantPrice) {
                    const priceStr = activeButton.dataset.variantPrice;
                    const priceValue = parseFloat(priceStr);
                    if (!Number.isNaN(priceValue)) {
                      // Price is in dollars, convert to cents
                      currentVariant.price = Math.round(priceValue * 100);
                      if (this.debug) {
                        console.log('[ProductGrouping] Got price from active button:', currentVariant.price);
                      }
                    }
                  }
                }
                
                // If still no price, try to get from product grouping data
                if ((!currentVariant.price || currentVariant.price === null || currentVariant.price === undefined) && 
                    this.currentVariantId && this.productGroupingData && this.currentProductId) {
                  const productData = this.productGroupingData[this.currentProductId];
                  if (productData && productData.variants) {
                    const variantFromData = productData.variants.find(
                      v => v.id.toString() === this.currentVariantId.toString()
                    );
                    if (variantFromData && variantFromData.price) {
                      currentVariant.price = variantFromData.price;
                      if (this.debug) {
                        console.log('[ProductGrouping] Got price from product grouping data:', currentVariant.price);
                      }
                    }
                  }
                }
              }
              
              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                addToCartButtonText,
                soldOutElement,
                true,
                currentVariant
              );
              if (currentVariant && typeof currentVariant.price !== "undefined") {
                const cents =
                  typeof currentVariant.price === "string"
                    ? parseInt(currentVariant.price, 10)
                    : currentVariant.price;
                if (!Number.isNaN(cents)) {
                  const dollars = cents / 100;
                  const priceForQuickAdd = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
                  if (priceForQuickAdd) {
                    this.updateQuickAddCTA(true, this.currentVariantId, priceForQuickAdd);
                  }
                }
              }

            }
          }
        })
        .catch((error) => {
          console.error(
            "[ProductGrouping] Failed to fetch initial variant availability:",
            error
          );
        });
    }

    //Force update the stock message on load
    const stockMessageEl = document.querySelector(
      'product-stock-message[data-context="pdp"]'
    );
    if (
      stockMessageEl &&
      this.currentVariantId &&
      typeof stockMessageEl.updateVariantId === "function"
    ) {
      stockMessageEl.updateVariantId(this.currentVariantId);
    }
  }
 
  _getVariantFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  _updateURLWithVariant(variantId) {
    if (this.isQuickView || !variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  _handleUpdateVariant(event, button) {
    event.preventDefault();
    if (this.selectionPending) {
      this._handlePendingSelection(button);
      return;
    }
    this._selectVariant({
      newVariantId: button.dataset.variantId,
      newVariantPrice: button.dataset.variantPrice,
      isAvailable: button.dataset.available === "true",
      optionIndex: button.dataset.optionIndex,
      newValue: button.dataset.value,
    });
  }

  _selectVariant({ newVariantId, newVariantPrice, isAvailable, optionIndex, newValue }) {

    if (this.debug)
      if (this.debug) {
        console.log("Handling variant update:", {
          newVariantId,
          newVariantPrice,
          isAvailable,
          optionIndex,
          newValue,
        });
      }

    // Track user's size preference when they explicitly select a size
    if (this._isOptionSize(optionIndex, newValue)) {
      this.preferredSize = newValue;
      this.hasUserSelectedSize = true;
      if (this.debug)
        console.log("User selected size preference:", this.preferredSize);
    }

    if (!newVariantId || !optionIndex || !newValue) {
      console.error("Missing required button dataset attributes:", {
        newVariantId,
        optionIndex,
        newValue,
      });
      return;
    }

    const addToCartForm = document.querySelector(
      ".product-info .shopify-product-form"
    );
    const input = addToCartForm?.querySelector('input[name="id"]');
    const soldOutElement = document.querySelector(
      ".product-info sold-out-badge"
    );

    if (!addToCartForm || !input) {
      console.error("Add to cart form or input not found:", {
        addToCartForm: !!addToCartForm,
        input: !!input,
      });
      return;
    }

    this.currentOptions[`option${optionIndex}`] = newValue;
    this.currentVariantId = newVariantId;
    input.value = newVariantId;
    this.updateVariantClass();
    this._updateURLWithVariant(newVariantId);

    if (this.debug) {
      console.log("Updated currentOptions:", this.currentOptions);
      console.log("Updated currentVariantId:", this.currentVariantId);
    }

    // Notify AMP Back in Stock app about variant change
    this._notifyAmpBackInStock({
      id: newVariantId,
      available: isAvailable,
      price: newVariantPrice,
    });

    // CRITICAL: Dispatch variant:change event for other systems (custom.js, etc.)
    // This ensures all parts of the system stay in sync
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          variant: {
            id: newVariantId,
            available: isAvailable,
            price: newVariantPrice,
          },
          formId: addToCartForm?.id,
        },
      })
    );

    const stockMessages = [
      ...this.parentComponent.querySelectorAll("product-stock-message"),
      ...(this.parentComponent
        .closest("quick-view-modal")
        ?.querySelectorAll("product-stock-message") || []),
    ];
    stockMessages.forEach((stockMessage) => {
      stockMessage.updateVariantId(newVariantId);
    });

    // Fetch real-time variant availability to ensure consistency
    const requestedVariantId = newVariantId;
    this._fetchVariantAvailability(newVariantId)
      .then((realTimeAvailable) => {
        if (String(this.currentVariantId) !== String(requestedVariantId)) {
          if (this.debug) {
            console.log(
              "[ProductGrouping] Ignoring stale availability response",
              { requestedVariantId, currentVariantId: this.currentVariantId }
            );
          }
          return;
        }

        const resolvedAvailable =
          realTimeAvailable == null ? isAvailable : realTimeAvailable;

        if (this.debug) {
          console.log("[ProductGrouping] Real-time availability fetched:", {
            variantId: newVariantId,
            realTimeAvailable,
            resolvedAvailable,
            cachedAvailable: isAvailable,
          });
        }
        // Re-query buttons fresh inside callback to avoid stale references
        // Don't filter by type="submit" since it may have been changed by third-party scripts
        const buyButtonsEl = addToCartForm?.querySelector("buy-buttons");
        const allButtons = buyButtonsEl
          ? Array.from(buyButtonsEl.querySelectorAll("button"))
          : [];

        if (this.debug) {
          console.log("[ProductGrouping] Found buttons:", {
            totalButtons: allButtons.length,
            buttons: allButtons.map((btn) => ({
              hasBisTrigger: btn.hasAttribute("data-bis-trigger"),
              name: btn.name,
              type: btn.type,
              id: btn.id,
              display: btn.style.display,
            })),
          });
        }

        const notifyMeBtn = allButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const addToCartButton = this._buyButtonsSubmitButton(allButtons);

        const addToCartButtonText = addToCartButton?.querySelector("div");

        // CRITICAL: Always ensure we have both buttons before proceeding
        if (!addToCartButton || !notifyMeBtn) {
          console.warn(
            "[ProductGrouping] Button elements not found, retrying...",
            { addToCartButton: !!addToCartButton, notifyMeBtn: !!notifyMeBtn }
          );
          // Retry once after a short delay
          setTimeout(() => {
            const retryBuyButtonsEl =
              addToCartForm?.querySelector("buy-buttons");
            const retryAllButtons = retryBuyButtonsEl
              ? Array.from(retryBuyButtonsEl.querySelectorAll("button"))
              : [];
            const retryNotifyMeBtn = retryAllButtons.find((btn) =>
              btn.hasAttribute("data-bis-trigger")
            );
            const retryAddToCartButton =
              this._buyButtonsSubmitButton(retryAllButtons);
            const retryAddToCartButtonText =
              retryAddToCartButton?.querySelector("div");

            if (retryAddToCartButton && retryNotifyMeBtn) {
              if (String(this.currentVariantId) !== String(requestedVariantId)) {
                return;
              }
              this._updateButtonVisibility(
                retryAddToCartButton,
                retryNotifyMeBtn,
                retryAddToCartButtonText,
                soldOutElement,
                resolvedAvailable
              );
            }
          }, 50);
          return;
        }

        // Update button visibility using helper method to ensure consistency
        const variantForPrice = {
          id: newVariantId,
          // newVariantPrice is usually in dollars ("398" or "398.00")
          price: typeof newVariantPrice === "string"
            ? Math.round(Number(newVariantPrice) * 100)
            : newVariantPrice
        };

        this._updateButtonVisibility(
          addToCartButton,
          notifyMeBtn,
          addToCartButtonText,
          soldOutElement,
          resolvedAvailable,
          variantForPrice   // 🔹pass price here
        );


        this.updateQuickAddCTA(
          resolvedAvailable,
          newVariantId,
          newVariantPrice
        );
        setTimeout(() => {
          document
            .querySelectorAll('product-stock-message[data-context="pdp"]')
            .forEach((el) => {
              el.hasFetched = false; // Force it to re-fetch
              el.updateVariantId(newVariantId);
            });
        }, 100);
      })
      .catch((error) => {
        console.error(
          "[ProductGrouping] Failed to fetch real-time variant availability, falling back to cached data:",
          error
        );

        // Fallback to original cached logic if API fails
        // Re-query buttons to ensure we have fresh references
        const fallbackBuyButtonsEl =
          addToCartForm?.querySelector("buy-buttons");
        const fallbackAllButtons = fallbackBuyButtonsEl
          ? Array.from(fallbackBuyButtonsEl.querySelectorAll("button"))
          : [];
        const fallbackNotifyMeBtn = fallbackAllButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const fallbackAddToCartButton =
          this._buyButtonsSubmitButton(fallbackAllButtons);
        const fallbackAddToCartButtonText =
          fallbackAddToCartButton?.querySelector("div");
          const variantForPrice = {
            id: newVariantId,
            price: typeof newVariantPrice === "string"
              ? Math.round(Number(newVariantPrice) * 100)
              : newVariantPrice
          };

        if (fallbackAddToCartButton && fallbackNotifyMeBtn) {
          this._updateButtonVisibility(
            fallbackAddToCartButton,
            fallbackNotifyMeBtn,
            fallbackAddToCartButtonText,
            soldOutElement,
            isAvailable,
            variantForPrice 
          );
        }

        this.updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice);
      });
  }

  /**
   * Central method to update button visibility - ensures consistent behavior
   * CRITICAL: This is the single source of truth for button state management
   */
  _updateButtonVisibility(
    addToCartButton,
    notifyMeBtn,
    addToCartButtonText,
    soldOutElement,
    isAvailable,
    variantForPrice = null
  ) {
    if (!addToCartButton || !notifyMeBtn) {
      console.error('[ProductGrouping] Cannot update button visibility - buttons not found');
      return;
    }

    if (this.debug) {
      console.log('[ProductGrouping] _updateButtonVisibility called', {
        isAvailable,
        hasSoldOutElement: !!soldOutElement,
        addToCartButtonDisplay: addToCartButton.style.display,
        notifyMeBtnDisplay: notifyMeBtn.style.display
      });
    }

    const isPreorder = window.isPreorderProduct?.() || false;

    // 🔹 Build price string - get it directly from the displayed price-list element
    let priceText = '';
    try {
      // Get price directly from the displayed price-list element (matches what user sees)
      // This avoids parsing/formatting issues and ensures it always matches the displayed price
      const getPriceFromPriceList = () => {
        const productInfo = document.querySelector('.product-info, [class*="product-info"]');
        const priceList = productInfo 
          ? productInfo.querySelector('price-list.price-list')
          : document.querySelector('price-list.price-list');
        
        if (priceList) {
          // Get the sale-price element (it shows the actual price whether on sale or not)
          const salePriceEl = priceList.querySelector('sale-price');
          const priceElement = salePriceEl || priceList.querySelector('.text-subdued');
          
          if (priceElement) {
            // Get text content and remove screen reader text
            let priceTextContent = priceElement.textContent?.trim() || '';
            
            // Use the Liquid-formatted price as-is so Markets currencies
            // (Afl, €, £, etc.) are not rewritten as USD.
            const srOnlySpans = priceElement.querySelectorAll('.sr-only');
            srOnlySpans.forEach(span => {
              priceTextContent = priceTextContent.replace(span.textContent, '').trim();
            });
            priceTextContent = priceTextContent.replace(/\s+/g, ' ').trim();
            if (priceTextContent) {
              if (this.debug) {
                console.log('[ProductGrouping] Got price text directly from sale-price element:', priceTextContent);
              }
              return priceTextContent;
            }
          }
        }
        return null;
      };
      
      // Try to get price immediately
      priceText = getPriceFromPriceList();
      
      // If not found, wait for price-list to update (variant changes can be async)
      // The price-list might be re-rendered when variants change, so we need to wait
      if (!priceText) {
        // Use requestAnimationFrame to wait for DOM updates, then retry multiple times
        let attempts = 0;
        const maxAttempts = 5;
        
        const tryGetPrice = () => {
          attempts++;
          // Always query fresh - price-list might be replaced during color changes
          const retryPrice = getPriceFromPriceList();
          
          if (retryPrice) {
            priceText = retryPrice;
            // Update button text if we got the price
            if (addToCartButtonText && isAvailable) {
              if (isPreorder) {
                addToCartButtonText.textContent = `PREORDER – ${retryPrice}`;
              } else {
                addToCartButtonText.textContent = `Add to Bag – ${retryPrice}`;
              }
            }
          } else if (attempts < maxAttempts) {
            // Retry with increasing delays
            setTimeout(tryGetPrice, 50 * attempts);
          }
        };
        
        // Start retry attempts after DOM updates
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(tryGetPrice, 50);
          });
        });
      }
      
      // Fallback: if price-list not available, try to format from variant data
      if (!priceText) {
        const variant = variantForPrice || this._getCurrentVariant?.();
        if (variant && typeof variant.price !== 'undefined' && variant.price !== null) {
          const cents =
            typeof variant.price === 'string'
              ? parseInt(variant.price, 10)
              : variant.price;
          if (!Number.isNaN(cents)) {
            // Use Shopify's money format from themeVariables for proper currency support
            const moneyFormat = window.themeVariables?.settings?.moneyFormat || '${{amount}}';
            const moneyWithCurrencyFormat = window.themeVariables?.settings?.moneyWithCurrencyFormat || '${{amount}} USD';
            const currencyCodeEnabled = window.themeVariables?.settings?.currencyCodeEnabled || false;
            
            const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
            const format = currencyCodeEnabled ? moneyWithCurrencyFormat : moneyFormat;
            priceText = format.replace('{{amount}}', amount);
            
            if (this.debug) {
              console.log('[ProductGrouping] Price text formatted from variant data:', priceText);
            }
          }
        }
      }
    } catch (err) {
      if (this.debug) {
        console.warn('[ProductGrouping] Error reading variant price for CTA label', err);
      }
    }

    if (isAvailable) {
      // Variant is available - ALWAYS show Add to Bag / PREORDER button
      if (addToCartButtonText) {
        if (isPreorder) {
          addToCartButtonText.textContent = priceText
            ? `PREORDER – ${priceText}`
            : 'PREORDER';
        } else {
          addToCartButtonText.textContent = priceText
            ? `Add to Bag – ${priceText}`
            : 'Add to Bag';
        }
      }

      // Ensure button visible/enabled
      addToCartButton.removeAttribute("disabled");
      addToCartButton.classList.remove("button--subdued");
      addToCartButton.setAttribute("type", "submit");
      addToCartButton.style.setProperty("display", "block", "important");
      addToCartButton.style.setProperty("visibility", "visible", "important");
      addToCartButton.style.setProperty("opacity", "1", "important");
      addToCartButton.style.removeProperty("pointer-events");

      // Hide waitlist
      notifyMeBtn.style.setProperty("display", "none", "important");
      notifyMeBtn.style.setProperty("visibility", "hidden", "important");
      notifyMeBtn.style.setProperty("opacity", "0", "important");

      if (soldOutElement) {
        soldOutElement.classList.add("is-hidden");
      }

      if (this.debug) {
        console.log("[ProductGrouping] Button visibility updated: available");
      }
    } else {
      // Variant is sold out - show Join Waitlist button
      this.showJoinWaitListButton(
        addToCartButton,
        notifyMeBtn,
        addToCartButtonText,
        soldOutElement
      );

      if (this.debug) {
        console.log(
          "[ProductGrouping] Button visibility updated: Join Waitlist shown"
        );
      }
    }
  }


  updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice) {
    if (this.debug) {
      console.log("updateQuickAddCTA called:", {
        isAvailable,
        newVariantId,
        newVariantPrice,
      });
    }
    document
      .querySelectorAll("product-quick-add, buy-buttons")
      .forEach((quickAdd) => {
        const $submit = quickAdd.querySelector(".buy-buttons__submit");
        // console.log('🚀 -> $submit:', $submit)
        const input = quickAdd.querySelector('input[name="id"]');
        // console.log('🚀 -> input:', input)
        if (input) input.value = newVariantId;

        if (isAvailable && $submit) {
          const isPreorder = document
            .querySelector("#main-product-info")
            ?.classList.contains("preorder");
          
          // Format price correctly - newVariantPrice comes from data-variant-price
          // The money_without_trailing_zeros filter should output dollars (e.g., "75" for $75, "179" for $179)
          // But we need to handle edge cases where it might be incorrectly formatted
          let formattedPrice = '';
          if (newVariantPrice) {
            let priceValue = typeof newVariantPrice === 'string' 
              ? parseFloat(newVariantPrice.replace(/[^0-9.]/g, '')) 
              : newVariantPrice;
            
            if (!Number.isNaN(priceValue) && priceValue > 0) {
              // Detect incorrectly formatted prices that have been divided by 100:
              // - Prices like 0.75, 1.79, 7.50 should be 75, 179, 750
              // - Rule: If price is less than 20 and has exactly 2 decimal places, it's likely been incorrectly divided
              //   (This avoids false positives for legitimate prices like $1.99, $2.50, etc. which are rare in this store)
              const originalPrice = priceValue;
              const hasDecimals = priceValue % 1 !== 0;
              const decimalPlaces = priceValue.toString().split('.')[1]?.length || 0;
              
              // More aggressive detection: if price is suspiciously low with decimals, correct it
              // This catches cases like 0.75, 1.79, 7.50, etc.
              if (priceValue < 20 && hasDecimals && decimalPlaces === 2) {
                // Price has 2 decimal places and is less than $20 - likely incorrectly formatted
                // Multiply by 100 to correct it (e.g., 1.79 -> 179, 0.75 -> 75, 7.50 -> 750)
                priceValue = Math.round(priceValue * 100);
                if (this.debug) {
                  console.log('[ProductGrouping] Price corrected from', originalPrice, 'to', priceValue, '(was incorrectly divided by 100)');
                }
              } else if (priceValue < 1 && priceValue > 0) {
                // Price less than 1 (e.g., 0.75) - definitely needs correction
                priceValue = Math.round(priceValue * 100);
                if (this.debug) {
                  console.log('[ProductGrouping] Price corrected from', originalPrice, 'to', priceValue, '(was < 1)');
                }
              }
              
              formattedPrice = formatMoneyWithTheme(priceValue);
            }
          }
          
          const newLabel = isPreorder
            ? (formattedPrice ? `PREORDER – ${formattedPrice}` : 'PREORDER')
            : (formattedPrice ? `Add to Bag – ${formattedPrice}` : 'Add to Bag');
          console.log("Setting new label:", newLabel);

          // Handle both structures: buttons with <div> wrapper (quick-add) and plain text buttons (PDP)
          const $labelElement = $submit.querySelector("div");
          if ($labelElement) {
            $labelElement.textContent = newLabel;
          } else {
            $submit.textContent = newLabel;
          }
          if (this.debug) {
            console.log("Label updated successfully");
          }
          $submit.setAttribute("type", "submit");
          $submit.removeEventListener("click", this._scrollToBuyButtons);
        } else if ($submit) {
          const $labelElement = $submit.querySelector("div");
          if ($labelElement) {
            $labelElement.textContent = "Join Waitlist";
          } else {
            $submit.textContent = "Join Waitlist";
          }
          $submit.setAttribute("type", "button");
          $submit.addEventListener("click", this._scrollToBuyButtons);
        }
      });
  }

  _setupWaitlistScroll() {
    const $submit = document.querySelectorAll("product-quick-add .buy-buttons__submit");
    $submit.forEach((submit) => {
      submit.addEventListener("click", this._scrollToBuyButtons);
    });
  }

  _scrollToBuyButtons(event) {
    event.preventDefault();
    const scrollToElement = document.querySelector('.product-info__buy-buttons');
    if (scrollToElement) {
      scrollToElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  updateVariantClass() {
    Object.values(this.currentOptions).forEach((data, index) => {
      // console.log(' this.currentOptions ', index, this.currentOptions)

      let optionIndex = index;
      if (this.currentOptions.option2.length) {
        optionIndex = optionIndex + 1
      }
      if (this.currentOptions.option3.length) {
        optionIndex = optionIndex + 1
      }
      // console.log('🚀 -> optionIndex:', optionIndex)
      const row = this.querySelector(`.product-grouping__options[data-options-row="${optionIndex}"]`);
      // console.log('🚀 -> row:', row)

      if (optionIndex === 1) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          button.removeAttribute('aria-checked');
        });
      }
      if (optionIndex === 2) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option3 === this.currentOptions.option3;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          button.removeAttribute('aria-checked');
        });
      }
      if (optionIndex === 3) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option2 === this.currentOptions.option2;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          button.removeAttribute('aria-checked');
        });
      }
    });
  }

  showJoinWaitListButton(addToCartButton, notifyMeBtn, addToCartButtonText, soldOutElement) {
    if (!addToCartButton || !notifyMeBtn) {
      console.warn(
        "[ProductGrouping] showJoinWaitListButton called with missing buttons"
      );
      return;
    }

    // CRITICAL: Completely hide and disable Add to Cart button
    // We need to be aggressive here to override any CSS rules
    addToCartButton.setAttribute("disabled", "disabled");
    addToCartButton.classList.add("button--subdued");
    addToCartButton.setAttribute("type", "button"); // Remove submit capability
    addToCartButton.style.setProperty("display", "none", "important");
    addToCartButton.style.setProperty("visibility", "hidden", "important");
    addToCartButton.style.setProperty("opacity", "0", "important");
    addToCartButton.style.setProperty("pointer-events", "none", "important");

    if (addToCartButtonText) {
      addToCartButtonText.textContent = "Sold Out";
    }

    // Show waitlist button with !important to override any interference
    notifyMeBtn.style.setProperty("display", "block", "important");
    notifyMeBtn.style.setProperty("visibility", "visible", "important");
    notifyMeBtn.style.setProperty("opacity", "1", "important");

    // Show sold out badge
    if (soldOutElement) {
      soldOutElement.classList.remove("is-hidden");
    }

    if (this.debug) {
      if (this.debug) {
        console.log("[ProductGrouping] Waitlist button shown for OOS variant");
      }
    }
  }

  async _fetchVariantStock(variantId) {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      console.error('[ProductGrouping] Missing Shopify token or store name');
      return 0;
    }

    const country = this._storefrontCountry();
    const query = country
      ? `
      query GetVariantStock($variantId: ID!, $country: CountryCode!) @inContext(country: $country) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            quantityAvailable
          }
        }
      }
    `
      : `
      query GetVariantStock($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            quantityAvailable
          }
        }
      }
    `;

    const variables = { variantId: `gid://shopify/ProductVariant/${variantId}` };
    if (country) variables.country = country;

    try {
      const response = await fetch(`https://${store}.myshopify.com/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Accept': 'application/json',
          'Accept-Language': locale,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.error(`[ProductGrouping] Shopify API error: ${response.status}`, await response.text());
        return 0;
      }

      const result = await response.json();
      const qty = result?.data?.node?.quantityAvailable ?? 0;
      return qty;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching stock quantity:', error);
      return 0;
    }
  }

  _getStorefrontApiConfig() {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      return null;
    }

    return { accessToken, store, locale, country: this._storefrontCountry() };
  }

  _storefrontCountry() {
    const country = window.Shopify?.country;
    return typeof country === 'string' && /^[A-Z]{2}$/.test(country) ? country : null;
  }

  async _fetchVariantsAvailabilityBatch(variantIds = []) {
    const ids = [...new Set((variantIds || []).map(String).filter(Boolean))];
    if (!ids.length) return {};

    const config = this._getStorefrontApiConfig();
    if (!config) {
      console.error('[ProductGrouping] Missing Shopify token or store name for availability check');
      return {};
    }

    const country = config.country;
    const query = country
      ? `
      query GetVariantsAvailability($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `
      : `
      query GetVariantsAvailability($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `;

    const variables = {
      ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`),
    };
    if (country) variables.country = country;

    try {
      const response = await fetch(
        `https://${config.store}.myshopify.com/api/2025-01/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': config.accessToken,
            Accept: 'application/json',
            'Accept-Language': config.locale,
          },
          body: JSON.stringify({ query, variables }),
        }
      );

      if (!response.ok) {
        console.error(
          `[ProductGrouping] Shopify API error for availability batch: ${response.status}`,
          await response.text()
        );
        return {};
      }

      const result = await response.json();
      if (result?.errors?.length) {
        console.warn(
          '[ProductGrouping] Availability batch query returned GraphQL errors:',
          result.errors
        );
        return {};
      }

      const availability = {};
      for (const node of result?.data?.nodes || []) {
        if (!node?.id) continue;
        const numericId = node.id.split('/').pop();
        availability[numericId] = node.availableForSale === true;
      }

      return availability;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching batch variant availability:', error);
      return {};
    }
  }

  _applySizeOptionAvailability(button, sellable) {
    button.dataset.available = sellable ? 'true' : 'false';
    button.classList.toggle('is-disabled', !sellable);
  }

  _applyInseamOptionAvailability(button, sellable) {
    button.dataset.available = sellable ? 'true' : 'false';
    button.classList.remove('is-disabled');
    button.classList.toggle('is-sold-out', !sellable);
  }

  async _syncSizeOptionAvailability() {
    const sizeButtons = Array.from(
      this.querySelectorAll(
        '.product-grouping__options.is-size button[js-update-variant][data-variant-id]'
      )
    );
    const inseamButtons = Array.from(
      this.querySelectorAll(
        '.product-grouping__options:not(.is-size) button[js-update-variant][data-variant-id]'
      )
    );
    const variantButtons = [...sizeButtons, ...inseamButtons];
    if (!variantButtons.length) return;

    const availability = await this._fetchVariantsAvailabilityBatch(
      variantButtons.map((button) => button.dataset.variantId)
    );

    if (this.selectionPending) {
      variantButtons.forEach((button) => {
        const variantId = button.dataset.variantId;
        if (!(variantId in availability)) return;
        button.dataset.available = availability[variantId] ? "true" : "false";
      });
      this.variantMatrix.forEach((variant) => {
        if (!(String(variant.id) in availability)) return;
        variant.available = availability[String(variant.id)];
      });
      this._renderPendingOptionStates();
      return;
    }

    sizeButtons.forEach((button) => {
      const variantId = button.dataset.variantId;
      if (!(variantId in availability)) return;
      this._applySizeOptionAvailability(button, availability[variantId]);
    });

    inseamButtons.forEach((button) => {
      const variantId = button.dataset.variantId;
      if (!(variantId in availability)) return;
      this._applyInseamOptionAvailability(button, availability[variantId]);
    });
  }

  _getVariantRows() {
    return Array.from(
      this.querySelectorAll(".product-grouping__options[data-options-row]")
    ).filter((row) => row.querySelector("button[js-update-variant]"));
  }

  _normalizeOptionValue(value) {
    return value == null
      ? ""
      : value.toString().replace(/["'\s]/g, "").toLowerCase();
  }

  /** Chosen value per variant option row, e.g. { 2: "32", 3: "30" }, optionally leaving one row out */
  _getChosenOptions(excludeIndex = null) {
    const chosen = {};
    this._getVariantRows().forEach((row) => {
      const index = row.dataset.optionsRow;
      const value = this.currentOptions[`option${index}`];
      if (index !== String(excludeIndex) && value) {
        chosen[index] = value;
      }
    });
    return chosen;
  }

  _getMatrixVariants(selections) {
    return this.variantMatrix.filter((variant) =>
      Object.entries(selections).every(
        ([index, value]) =>
          this._normalizeOptionValue(variant[`option${index}`]) ===
          this._normalizeOptionValue(value)
      )
    );
  }

  /**
   * Start with nothing chosen, except rows with a single value (nothing to choose) and values carried over from the
   * product the shopper swapped away from when this product offers them
   */
  _initPendingSelection(carryOver) {
    this._getVariantRows().forEach((row) => {
      const index = row.dataset.optionsRow;
      const values = [
        ...new Set(
          Array.from(row.querySelectorAll("button[js-update-variant]")).map(
            (button) => button.dataset.value
          )
        ),
      ];
      const carried = carryOver?.[`option${index}`];
      const carriedValue =
        carried &&
        values.find(
          (value) =>
            this._normalizeOptionValue(value) ===
            this._normalizeOptionValue(carried)
        );
      if (values.length === 1) {
        this.currentOptions[`option${index}`] = values[0];
      } else if (carriedValue) {
        this.currentOptions[`option${index}`] = carriedValue;
      } else {
        this.currentOptions[`option${index}`] = "";
      }
    });

    const chosen = this._getChosenOptions();
    if (!this._getMatrixVariants(chosen).length) {
      // Carried-over values that no variant combines: keep the first, drop the rest
      Object.keys(chosen)
        .slice(1)
        .forEach((index) => (this.currentOptions[`option${index}`] = ""));
    }

    this._renderPendingOptionStates();

    const missingRow = this._getMissingRow();
    if (!missingRow) {
      // Carried-over values complete the selection; wait for the swapped-in buy buttons to upgrade
      queueMicrotask(() => {
        const [variant] = this._getMatrixVariants(this._getChosenOptions());
        const [index, value] = Object.entries(this._getChosenOptions()).pop() || [];
        if (variant && index) this._completePendingSelection(variant, index, value);
      });
    } else if (carryOver) {
      queueMicrotask(() => this._setSelectionPendingCTA(missingRow));
    }
  }

  _getMissingRow() {
    return this._getVariantRows().find(
      (row) => !this.currentOptions[`option${row.dataset.optionsRow}`]
    );
  }

  /**
   * A click while some option is still unchosen only records the choice and narrows the other rows. Choices in other
   * rows that no variant combines with the new one are dropped. The variant is selected once every row is chosen.
   */
  _handlePendingSelection(button) {
    const index = button.dataset.optionIndex;
    const value = button.dataset.value;
    if (!index || !value) return;

    this.currentOptions[`option${index}`] = value;
    if (this._isOptionSize(index, value)) {
      this.preferredSize = value;
    }

    const kept = { [index]: value };
    Object.entries(this._getChosenOptions(index)).forEach(([otherIndex, otherValue]) => {
      const candidate = { ...kept, [otherIndex]: otherValue };
      if (this._getMatrixVariants(candidate).length) {
        kept[otherIndex] = otherValue;
      } else {
        this.currentOptions[`option${otherIndex}`] = "";
      }
    });

    const missingRow = this._getMissingRow();
    const [variant] = missingRow ? [] : this._getMatrixVariants(kept);
    if (variant) {
      this._completePendingSelection(variant, index, value);
      return;
    }

    this._renderPendingOptionStates();
    this._setSelectionPendingCTA(missingRow);
  }

  _completePendingSelection(variant, optionIndex, value) {
    this.selectionPending = false;
    this.removeAttribute("data-selection-pending");
    this.hasUserSelectedSize = true;

    // Back to per-variant availability; updateVariantClass() then filters the rows by the chosen options
    this.querySelectorAll("button[js-update-variant]").forEach((button) => {
      const sellable = button.dataset.available === "true";
      if (button.closest(".product-grouping__options.is-size")) {
        this._applySizeOptionAvailability(button, sellable);
      } else {
        this._applyInseamOptionAvailability(button, sellable);
      }
    });

    this._getBuyButtons()?.clearSelectionPending?.();

    this._selectVariant({
      newVariantId: String(variant.id),
      newVariantPrice: variant.price,
      isAvailable: variant.available === true,
      optionIndex: String(optionIndex),
      newValue: value,
    });
  }

  _getBuyButtons() {
    return (
      this.closest(".product-info")?.querySelector("buy-buttons") ||
      document.querySelector(".product-info .shopify-product-form buy-buttons")
    );
  }

  _setSelectionPendingCTA(missingRow) {
    let label = this.dataset.selectSizeLabel;
    if (missingRow && !missingRow.classList.contains("is-size")) {
      const optionName = missingRow
        .querySelector(".product-grouping__option__name")
        ?.textContent.trim();
      if (optionName) {
        label = (this.dataset.selectOptionLabel || "").replace("[option]", optionName);
      }
    }
    this._getBuyButtons()?.setSelectionPending?.(label);
  }

  /**
   * While selection is pending each value shows a single button (the waist + inseam grid renders one per
   * combination). A value is hidden when no variant combines it with the other chosen options, and crossed out when
   * none of those variants is in stock. Out-of-stock values stay clickable so the shopper can join the waitlist.
   */
  _renderPendingOptionStates() {
    this._getVariantRows().forEach((row) => {
      const index = row.dataset.optionsRow;
      const others = this._getChosenOptions(index);
      const chosen = this._normalizeOptionValue(this.currentOptions[`option${index}`]);
      const isSizeRow = row.classList.contains("is-size");
      const buttons = Array.from(row.querySelectorAll("button[js-update-variant]"));
      const handledValues = new Set();

      buttons.forEach((button) => {
        const value = this._normalizeOptionValue(button.dataset.value);
        if (handledValues.has(value)) return;
        handledValues.add(value);

        const sameValueButtons = buttons.filter(
          (other) => this._normalizeOptionValue(other.dataset.value) === value
        );
        const variants = this._getMatrixVariants({ ...others, [index]: button.dataset.value });
        const variantIds = new Set(variants.map((variant) => String(variant.id)));
        const representative =
          sameValueButtons.find((other) => variantIds.has(other.dataset.variantId)) ||
          sameValueButtons[0];
        const available = variants.some((variant) => variant.available === true);
        const isActive = chosen !== "" && chosen === value;

        sameValueButtons.forEach((other) => {
          const shown = other === representative && variants.length > 0;
          other.classList.toggle("is-hidden", !shown);
          other.classList.toggle("is-active", shown && isActive);
          other.setAttribute("aria-pressed", shown && isActive ? "true" : "false");
        });

        if (isSizeRow) {
          representative.classList.toggle("is-disabled", !available);
        } else {
          representative.classList.remove("is-disabled");
          representative.classList.toggle("is-sold-out", !available);
        }
      });
    });
  }

  async _fetchVariantAvailability(variantId) {
    const availability = await this._fetchVariantsAvailabilityBatch([variantId]);
    if (!(String(variantId) in availability)) {
      return false;
    }

    const available = availability[String(variantId)];

    if (this.debug) {
      console.log('[ProductGrouping] API Response for variant', variantId, ':', {
        availableForSale: available,
      });
    }

    return available;
  }

  _notifyAmpBackInStock(variant) {
    try {
      // Method 1: Update the variant input that the app might be watching
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variant.id) {
        variantInput.value = variant.id;
        // Trigger change event to notify any listeners
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Method 2: Update data attributes that the app reads when the modal opens
      const bisButtons = document.querySelectorAll("[data-bis-trigger]");
      bisButtons.forEach((button) => {
        button.setAttribute("data-variant-id", variant.id);
        button.setAttribute(
          "data-product-id",
          variant.product_id || window.meta?.product?.id || ""
        );
      });
    } catch (error) {
      console.warn(
        "[ProductGrouping] Error notifying AMP Back in Stock app:",
        error
      );
    }
  }

  _getGroupingFamilyKey() {
    const h =
      this._getLiveProductGroupingRoot()?.getAttribute(
        "data-current-product-handle"
      ) ||
      this.currentProductHandle ||
      "";
    if (h.includes("inlet-knit-blazer")) return "inlet-knit-blazer";
    if (h.includes("all-day-short")) return "all-day-short";
    if (h.startsWith("legend-sweater-shirt")) return "legend-sweater-shirt";
    return null;
  }

  /**
   * After #main-product-info is replaced, `this` may be a detached <product-grouping>.
   * Read swatches / attributes from the connected instance in the document.
   */
  _getLiveProductGroupingRoot() {
    if (this.isQuickView) {
      const modal = this.closest?.("quick-view-modal");
      return (
        modal?.querySelector?.("product-grouping") ||
        (this.isConnected ? this : null)
      );
    }
    return (
      document.querySelector("#main-product-info product-grouping") ||
      (this.isConnected ? this : null)
    );
  }

  /** Shopify handle segment immediately after this slug (e.g. all-day-short → 2-0-7 or 7). */
  _familyProductHandleSlug(family) {
    if (family === "all-day-short") return "all-day-short";
    if (family === "inlet-knit-blazer") return "inlet-knit-blazer";
    if (family === "legend-sweater-shirt") return "legend-sweater-shirt";
    return null;
  }

  /**
   * Legend™ Sweater Shirt: double-pocket handles are `legend-sweater-shirt-{color}` with optional
   * numeric suffix (e.g. `-2`); single-pocket uses `legend-sweater-shirt-single-pocket-{color}`.
   */
  _extractLegendPocketSegment(handle, colorSeg) {
    const slug = "legend-sweater-shirt";
    if (!handle?.startsWith(`${slug}-`) || !colorSeg) return null;
    const singlePrefix = `${slug}-single-pocket-`;
    if (handle.startsWith(singlePrefix)) {
      const rest = handle.slice(singlePrefix.length);
      if (rest === colorSeg || rest.startsWith(`${colorSeg}-`)) return "single-pocket";
      return null;
    }
    const afterSlug = handle.slice(slug.length + 1);
    if (afterSlug === colorSeg || afterSlug.startsWith(`${colorSeg}-`)) return "";
    return null;
  }

  _extractGroupingHandleSegment(handle, family, familySlug, colorSeg) {
    if (!handle || !familySlug || !colorSeg) return null;
    if (family === "legend-sweater-shirt") {
      return this._extractLegendPocketSegment(handle, colorSeg);
    }
    return this._extractSegmentBetweenFamilyAndColor(
      handle,
      familySlug,
      colorSeg
    );
  }

  /**
   * Middle part of product handle between family slug and color suffix, e.g.
   * all-day-short-2-0-7-mountain-burgundy + color mountain-burgundy → "2-0-7"
   * (avoids wrong all-day-short-7-khaki vs all-day-short-2-0-7-khaki when mixing colors).
   */
  _extractSegmentBetweenFamilyAndColor(handle, familySlug, colorSegment) {
    if (!handle || !familySlug || !colorSegment) return null;
    const start = `${familySlug}-`;
    const end = `-${colorSegment}`;
    if (!handle.startsWith(start) || !handle.endsWith(end)) return null;
    return handle.slice(start.length, handle.length - end.length);
  }

  _handleSegmentFromPersistEntry(entry, familySlug) {
    if (typeof entry?.handleSegment === "string") return entry.handleSegment;
    if (entry?.prefix === familySlug) return "";
    if (entry?.prefix && familySlug && entry.prefix.startsWith(`${familySlug}-`)) {
      return entry.prefix.slice(familySlug.length + 1);
    }
    return null;
  }

  _handleizeMerchLabel(name) {
    if (!name || typeof name !== "string") return "";
    return name
      .trim()
      .toLowerCase()
      .replace(/['"™®]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  _colorSegmentFromActiveSwatch(root) {
    const el = root || this._getLiveProductGroupingRoot() || this;
    const active = el.querySelector?.(
      ".product-swatch[js-request-new-product].is-active"
    );
    if (!active?.dataset?.colorName) return null;
    return this._handleizeMerchLabel(active.dataset.colorName);
  }

  _findActiveSubOptionValueForRow(rowLabelNeedle, root) {
    const el = root || this._getLiveProductGroupingRoot() || this;
    const needle = rowLabelNeedle.toLowerCase();
    const sections = el.querySelectorAll?.(".product-grouping__option") || [];
    for (const section of sections) {
      const nameEl = section.querySelector(".product-grouping__option__name");
      const h4 = nameEl?.querySelector("h4") || nameEl;
      const text = (h4?.textContent || "").trim().toLowerCase();
      if (!text || !text.includes(needle)) continue;
      const active = section.querySelector(
        "button[js-update-sub-option].is-active"
      );
      const v = active?.dataset?.subOption;
      if (v) return v;
    }
    return null;
  }

  async _productJsonExists(handle) {
    if (!handle) return false;
    try {
      const res = await fetch(
        `${window.location.origin}/products/${encodeURIComponent(handle)}.json`,
        { headers: { Accept: "application/json" } }
      );
      return res.ok;
    } catch {
      return false;
    }
  }

  _pageHandleSegmentForPersistedCompare(family, familySlug) {
    const root = this._getLiveProductGroupingRoot() || this;
    const handle =
      root?.getAttribute?.("data-current-product-handle") ||
      this.currentProductHandle;
    const pageColor = this._colorSegmentFromActiveSwatch(root);
    if (!handle || !pageColor || !familySlug || !family) return null;
    return this._extractGroupingHandleSegment(
      handle,
      family,
      familySlug,
      pageColor
    );
  }

  async _maybeApplyPersistedSubOptionProductHandle(option, proposedHandle) {
    if (!option?.classList?.contains("product-swatch")) return proposedHandle;

    const colorSeg = this._handleizeMerchLabel(option.dataset.colorName);
    const activeSubs = Array.from(
      (this._getLiveProductGroupingRoot() || this).querySelectorAll(
        "button[js-update-sub-option].is-active"
      )
    )
      .map((btn) => btn.dataset?.subOption)
      .filter(Boolean);

    const allOptionsData = this.dataProductGroup?.all_options_data;
    if (colorSeg && activeSubs.length && allOptionsData) {
      const rows = allOptionsData
        .split(",")
        .map((row) => row.trim())
        .filter(Boolean);
      for (const row of rows) {
        const parts = row.split("||");
        if (parts.length < 3 || parts[2] !== colorSeg) continue;
        const rowOptions = (parts[0] || "").split("@@").filter(Boolean);
        if (rowOptions.length < activeSubs.length) continue;
        if (!activeSubs.every((val, i) => rowOptions[i] === val)) continue;
        if (parts[1] && parts[1] !== proposedHandle) {
          if (this.debug) {
            console.log(
              "[ProductGrouping] Swatch handle aligned to active sub-options:",
              proposedHandle,
              "->",
              parts[1]
            );
          }
          proposedHandle = parts[1];
        }
        break;
      }
    }

    const family = this._getGroupingFamilyKey();
    if (!family || typeof sessionStorage === "undefined") return proposedHandle;

    const dimKey =
      family === "inlet-knit-blazer"
        ? "length"
        : family === "all-day-short"
          ? "inseam"
          : family === "legend-sweater-shirt"
            ? "pocket"
            : null;
    if (!dimKey) return proposedHandle;

    const familySlug = this._familyProductHandleSlug(family);
    if (!familySlug || !colorSeg) return proposedHandle;

    const proposedSeg = this._extractGroupingHandleSegment(
      proposedHandle,
      family,
      familySlug,
      colorSeg
    );
    const pageSeg = this._pageHandleSegmentForPersistedCompare(family, familySlug);

    // Swatch already matches the PDP's inseam/length/pocket — do not let stale sessionStorage
    // downgrade to an older segment (e.g. 2-0-5) after Liquid fixed the attribute (2-0-7).
    // Inlet standard length uses no handle token (segment ""), so use null checks, not truthiness.
    if (
      proposedSeg !== null &&
      proposedSeg !== undefined &&
      pageSeg !== null &&
      pageSeg !== undefined &&
      proposedSeg === pageSeg
    ) {
      return proposedHandle;
    }

    let store = {};
    try {
      const raw = sessionStorage.getItem(ProductGrouping.SUBOPT_PREFS_KEY);
      if (raw) store = JSON.parse(raw) || {};
    } catch {
      return proposedHandle;
    }

    const entry = store[family];
    if (!entry?.value || entry.dim !== dimKey) return proposedHandle;

    const segment = this._handleSegmentFromPersistEntry(entry, familySlug);
    if (segment === null || segment === undefined) return proposedHandle;
    if (
      family !== "inlet-knit-blazer" &&
      family !== "legend-sweater-shirt" &&
      segment === ""
    ) {
      return proposedHandle;
    }

    const preferred = this._preferredProductHandleForSuboptionSegment(
      family,
      familySlug,
      segment,
      colorSeg
    );
    if (!preferred || preferred === proposedHandle) return proposedHandle;

    if (await this._productJsonExists(preferred)) {
      if (this.debug) {
        console.log(
          "[ProductGrouping] Swatch handle overridden by persisted sub-option:",
          proposedHandle,
          "->",
          preferred
        );
      }
      return preferred;
    }
    return proposedHandle;
  }

  /**
   * Compose canonical product handle from persisted length/inseam/pocket segment.
   * Inlet standard length has no token (inlet-knit-blazer-{color}); tall/short are explicit.
   * Legend double-pocket has no token (legend-sweater-shirt-{color}); single uses single-pocket.
   */
  _preferredProductHandleForSuboptionSegment(family, familySlug, segment, colorSeg) {
    if (!familySlug || !colorSeg) return null;
    if (family === "inlet-knit-blazer") {
      if (segment === "tall" || segment === "short") {
        return `${familySlug}-${segment}-${colorSeg}`;
      }
      return `${familySlug}-${colorSeg}`;
    }
    if (family === "legend-sweater-shirt") {
      if (!segment) return `${familySlug}-${colorSeg}`;
      return `${familySlug}-${segment}-${colorSeg}`;
    }
    if (!segment) return null;
    return `${familySlug}-${segment}-${colorSeg}`;
  }

  _persistSubOptionGroupingPreferenceFromDom() {
    if (typeof sessionStorage === "undefined") return;
    const root = this._getLiveProductGroupingRoot();
    const family = this._getGroupingFamilyKey();
    if (!family) return;

    const dimKey =
      family === "inlet-knit-blazer"
        ? "length"
        : family === "all-day-short"
          ? "inseam"
          : family === "legend-sweater-shirt"
            ? "pocket"
            : null;
    if (!dimKey) return;

    const value = this._findActiveSubOptionValueForRow(dimKey, root);
    if (!value) return;

    const colorSeg = this._colorSegmentFromActiveSwatch(root);
    if (!colorSeg) return;

    const handle =
      root?.getAttribute?.("data-current-product-handle") ||
      this.currentProductHandle;
    if (!handle) return;

    const familySlug = this._familyProductHandleSlug(family);
    if (!familySlug) return;

    const handleSegment = this._extractGroupingHandleSegment(
      handle,
      family,
      familySlug,
      colorSeg
    );
    if (handleSegment === null || handleSegment === undefined) return;
    if (familySlug === "all-day-short" && handleSegment === "") return;

    const prefix =
      (familySlug === "inlet-knit-blazer" || familySlug === "legend-sweater-shirt") &&
      handleSegment === ""
        ? familySlug
        : `${familySlug}-${handleSegment}`;

    try {
      const raw = sessionStorage.getItem(ProductGrouping.SUBOPT_PREFS_KEY);
      const store = raw ? JSON.parse(raw) || {} : {};
      store[family] = {
        dim: dimKey,
        value,
        prefix,
        handleSegment,
      };
      sessionStorage.setItem(
        ProductGrouping.SUBOPT_PREFS_KEY,
        JSON.stringify(store)
      );
      if (this.debug) {
        console.log("[ProductGrouping] Persisted sub-option preference:", {
          family,
          dimKey,
          value,
          prefix,
          handleSegment,
        });
      }
    } catch (e) {
      if (this.debug)
        console.warn("[ProductGrouping] Could not persist sub-option prefs:", e);
    }
  }

  async _handleOptionChange(event, option) {
    event.preventDefault();
    if (option.classList.contains('is-disabled')) return;

    const handleFromDom = option.dataset.productHandle || "";
    let newProduct = handleFromDom;
    newProduct = await this._maybeApplyPersistedSubOptionProductHandle(
      option,
      newProduct
    );
    if (!newProduct) newProduct = handleFromDom;

    const loadedIds = this.dataProductGroup?.loaded_ids?.replace(/"/g, '') || false;

    if (newProduct && newProduct !== this.currentProductHandle) {
      const requestId = ++this.productRequestId;
      this.productRequestController?.abort();
      this.productRequestController = new AbortController();
      try {
        this.parentComponent.classList.add("is-loading");

        // Capture current option2 and option3 values to preserve across color changes.
        // Partial choices never pick a variant in the new product; they are carried over instead.
        const currentVariant = this.selectionPending ? null : this._getCurrentVariant();
        const targetOption2 = currentVariant?.option2;
        const targetOption3 = currentVariant?.option3;

        // Performance: Check cache first to avoid redundant fetches
        let productData;
        if (this.productCache.has(newProduct)) {
          productData = this.productCache.get(newProduct);
          if (this.debug)
            console.log(
              "[ProductGrouping] Using cached product data for:",
              newProduct
            );
        } else {
          // First, get the new product data to find matching variant
          let previewUrl = new URL(
            window.location.origin + `/products/${newProduct}.json`
          );
          const productResponse = await fetch(previewUrl, {
            signal: this.productRequestController.signal,
          });

          // Validate response before parsing
          if (!productResponse.ok) {
            console.error(
              `[ProductGrouping] Failed to fetch product: ${productResponse.status}`
            );
            throw new Error(`Product fetch failed: ${productResponse.status}`);
          }

          const contentType = productResponse.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            console.error(
              `[ProductGrouping] Invalid content-type: ${contentType}`
            );
            throw new Error("Product response is not JSON");
          }

          productData = await productResponse.json();
          if (requestId !== this.productRequestId) return;

          // Cache for future clicks
          this.productCache.set(newProduct, productData);
          if (this.debug)
            console.log(
              "[ProductGrouping] Cached product data for:",
              newProduct
            );
        }

        // Find variant with matching option2/option3 in the new product
        let targetVariantId = null;
        if (
          productData.product &&
          productData.product.variants &&
          (targetOption2 || targetOption3)
        ) {
          targetVariantId = this._findMatchingVariant(
            productData.product.variants,
            targetOption2,
            targetOption3
          );
        }

        let url = new URL(window.location.origin + `/products/${newProduct}`);
        let params = new URLSearchParams();
        params.set("no-fade-in", "true");
        if (this.isQuickView) {
          // Quick view keeps preselecting a variant when the size doesn't carry over
          params.set("is-quick-view", "true");
        }
        if (loadedIds) {
          params.set("product-group-ids", loadedIds);
        }

        // If we found a matching variant, include it in the request so the new product renders with correct size
        if (targetVariantId) {
          params.set("variant", targetVariantId);
          if (this.debug)
            console.log("Including variant in AJAX request:", targetVariantId);
        }

        url.search = params.toString();
        const decodedURL = url.toString().replace(/%2B/g, "+");
        if (this.debug) console.log("decodedURL:", decodedURL);

        // Performance: Cache HTML to avoid redundant fetches
        const cacheKey = `${newProduct}-${targetVariantId || "default"}`;
        let text;
        if (this.htmlCache.has(cacheKey)) {
          text = this.htmlCache.get(cacheKey);
          if (this.debug)
            console.log("[ProductGrouping] Using cached HTML for:", cacheKey);
        } else {
          const response = await fetch(decodedURL, {
            signal: this.productRequestController.signal,
          });
          text = await response.text();
          if (requestId !== this.productRequestId) return;

          // Cache for future clicks
          this.htmlCache.set(cacheKey, text);
          if (this.debug)
            console.log("[ProductGrouping] Cached HTML for:", cacheKey);
        }

        const parser = new DOMParser();
        const newDocument = parser.parseFromString(text, "text/html");
        if (requestId !== this.productRequestId) return;

        const newMainProduct = newDocument.querySelector("#main-product-info");
        const currentMainProduct = this.isQuickView
          ? document.querySelector("quick-view-modal .quick-add-modal")
          : document.querySelector("#main-product-info");

        this.firstVariantId =
          newMainProduct?.getAttribute("data-first-variant");
        this.productId = newMainProduct?.getAttribute("data-product-id");
        this.currentProductHandle = newProduct;
        this.currentProductId = this.productId;

        ProductGrouping.pendingCarryOver = { ...this.currentOptions };
        if (newMainProduct && currentMainProduct) {
          this.isQuickView
            ? (currentMainProduct.innerHTML = newMainProduct.innerHTML)
            : currentMainProduct.replaceWith(newMainProduct);
        }
        ProductGrouping.pendingCarryOver = null;

        // No size to preserve: the new product loads with nothing chosen and its own instance handles the choices
        if (
          newMainProduct?.querySelector('product-grouping[data-selection-pending="true"]')
        ) {
          this._finishProductSwap({ newMainProduct, newDocument, newProduct, url, params, loadedIds });
          return;
        }

        // Resolve selected variant *after* new markup is in the document.
        // When cross-product size labels differ (e.g. L vs LT), targetVariantId may be null; using
        // data-first-variant (Shopify variants.first) can be an OOS row (e.g. MT) while Liquid defaulted
        // the form to first *available* (LT). That mismatch shows Join Waitlist until another size click.
        if (targetVariantId) {
          this.currentVariantId = targetVariantId.toString();
        } else {
          const renderedInput = document.querySelector(
            '.product-info input[name="id"]'
          );
          const renderedId = renderedInput?.value;
          const renderedOk =
            renderedId &&
            productData.product?.variants?.some(
              (v) => v.id.toString() === renderedId.toString()
            );
          if (renderedOk) {
            this.currentVariantId = renderedId.toString();
          } else {
            const firstAvailable = productData.product?.variants?.find(
              (v) =>
                v.available === true ||
                v.available === "true" ||
                v.available === 1
            );
            this.currentVariantId =
              (firstAvailable && firstAvailable.id.toString()) ||
              this.firstVariantId;
          }
          if (this.debug) {
            console.log(
              "[ProductGrouping] No exact option match across products; resolved variant id:",
              this.currentVariantId,
              { renderedId, renderedOk }
            );
          }
        }

        // Update the variant input field with the correct variant ID
        const variantInput = document.querySelector(
          '.product-info input[name="id"]'
        );
        if (variantInput && this.currentVariantId) {
          variantInput.value = this.currentVariantId;
        }

        // Re-bind variant buttons
        this._rebindVariantButtons();

        // Update product grouping data from the new HTML
        this.dataProductGroup = JSON.parse(
          this.querySelector("script[data-product-group]")?.textContent || "{}"
        );

        // Rebuild productGroupingData to include the new product's variant data
        const scriptTagsData = this.querySelectorAll(
          "script[js-product-group-data]"
        );
        this.productGroupingData = Array.from(scriptTagsData).reduce(
          (acc, script) => {
            const data = JSON.parse(script.textContent || "{}");
            const key = script.getAttribute("data-product-id");
            if (!acc[key]) {
              acc[key] = { tag_options: [], variants: [] };
            }
            const existingData = acc[key];
            existingData.tag_options.push(...data.tag_options);
            const uniqueVariants = data.variants.filter(
              (newVariant) =>
                !existingData.variants.some(
                  (existingVariant) => existingVariant.id === newVariant.id
                )
            );
            existingData.variants.push(...uniqueVariants);
            return acc;
          },
          this.productGroupingData || {}
        );

        // Also add the new product data directly from our API fetch
        if (productData.product && this.currentProductId) {
          this.productGroupingData[this.currentProductId] = {
            tag_options: [],
            variants: productData.product.variants || [],
          };
        }

        // Update currentOptions to reflect the selected variant using data from API
        const selectedVariant = productData.product?.variants?.find(
          (v) => v.id.toString() === this.currentVariantId.toString()
        );
        if (selectedVariant) {
          this.currentOptions.option2 =
            selectedVariant.option2 || this.currentOptions.option2;
          this.currentOptions.option3 =
            selectedVariant.option3 || this.currentOptions.option3;
        }

        // Update the visual state of size/option buttons to reflect the selected variant
        this.updateVariantClass();

        // CRITICAL: Wait for DOM to fully render before querying buttons
        // Use requestAnimationFrame to ensure the replaced HTML is painted
        requestAnimationFrame(() => {
          // Reset button state for the new product and check availability
          const newAddToCartForm = document.querySelector(
            ".product-info .shopify-product-form"
          );
          const newSoldOutElement = document.querySelector(
            ".product-info sold-out-badge"
          );

          // Get the currently selected waist and inseam from DOM (what user actually selected)
          // Priority: 1. Active DOM buttons, 2. Current variant, 3. Preserved values, 4. currentOptions
          let selectedWaist = null;
          let selectedInseam = null;
          
          // First: Try to get from active variant buttons in DOM (most reliable - what user actually selected)
          const activeWaistButton = document.querySelector('button[js-update-variant][data-option2].is-active, button[js-update-variant][data-option2][aria-checked="true"]');
          const activeInseamButton = document.querySelector('button[js-update-variant][data-option3].is-active, button[js-update-variant][data-option3][aria-checked="true"]');
          
          if (activeWaistButton) {
            selectedWaist = activeWaistButton.dataset.option2;
          }
          if (activeInseamButton) {
            selectedInseam = activeInseamButton.dataset.option3;
          }
          
          // Second: Try to get from the currently selected variant in the form
          if (!selectedWaist || !selectedInseam) {
            const variantInput = document.querySelector('.product-info input[name="id"]');
            if (variantInput && variantInput.value) {
              const currentVariantId = variantInput.value;
              const currentVariant = productData.product?.variants?.find(
                v => v.id.toString() === currentVariantId.toString()
              ) || this.productGroupingData[this.currentProductId]?.variants?.find(
                v => v.id.toString() === currentVariantId.toString()
              );
              
              if (currentVariant) {
                if (!selectedWaist) selectedWaist = currentVariant.option2;
                if (!selectedInseam) selectedInseam = currentVariant.option3;
              }
            }
          }
          
          // Third: Fallback to preserved values from before color change
          if (!selectedWaist) {
            selectedWaist = targetOption2;
          }
          if (!selectedInseam) {
            selectedInseam = targetOption3;
          }
          
          // Final fallback to currentOptions
          if (!selectedWaist) {
            selectedWaist = this.currentOptions.option2;
          }
          if (!selectedInseam) {
            selectedInseam = this.currentOptions.option3;
          }
          
          if (this.debug) {
            console.log('[ProductGrouping] Selected size for variant search:', {
              waist: selectedWaist,
              inseam: selectedInseam,
              source: {
                fromDOMButtons: {
                  waist: !!activeWaistButton,
                  inseam: !!activeInseamButton
                },
                fromPreserved: {
                  waist: selectedWaist === targetOption2,
                  inseam: selectedInseam === targetOption3
                }
              }
            });
          }
          
          // FIRST: Check cached data to find available variant matching BOTH waist and inseam
          // This is faster and more reliable than waiting for real-time check
          let firstAvailableVariantFromCache = null;
          if (productData.product?.variants && selectedWaist && selectedInseam) {
            // Use normalization to handle quotes and whitespace differences
            const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
            const normalizedSelectedWaist = normalizeValue(selectedWaist);
            const normalizedSelectedInseam = normalizeValue(selectedInseam);
            
            if (this.debug) {
              // Show sample of variants to see their actual values
              const sampleVariants = productData.product.variants.slice(0, 10).map(v => ({
                id: v.id,
                option2: v.option2,
                option3: v.option3,
                available: v.available,
                normalized: {
                  option2: normalizeValue(v.option2),
                  option3: normalizeValue(v.option3)
                }
              }));
              
              // Find all variants with matching waist to see what inseams are available
              const waistMatches = productData.product.variants.filter(v => {
                const normalizedVariantWaist = normalizeValue(v.option2);
                return normalizedVariantWaist === normalizedSelectedWaist;
              });
              
              console.log('[ProductGrouping] Cached check - searching for variant:', {
                selectedWaist,
                selectedInseam,
                normalizedSelectedWaist,
                normalizedSelectedInseam,
                totalVariants: productData.product.variants.length,
                sampleVariants,
                waistMatchesCount: waistMatches.length,
                waistMatches: waistMatches.map(v => ({
                  id: v.id,
                  option2: v.option2,
                  option3: v.option3,
                  available: v.available,
                  normalizedOption3: normalizeValue(v.option3),
                  matchesInseam: normalizeValue(v.option3) === normalizedSelectedInseam
                }))
              });
            }
            
            firstAvailableVariantFromCache = productData.product.variants.find(
              (v) => {
                // Normalize variant values for comparison
                const normalizedVariantWaist = normalizeValue(v.option2);
                const normalizedVariantInseam = normalizeValue(v.option3);
                
                const matchesWaist = normalizedVariantWaist === normalizedSelectedWaist;
                const matchesInseam = normalizedVariantInseam === normalizedSelectedInseam;
                const isAvailable = v.available === true || v.available === "true" || v.available === 1 || v.available === "1";
                
                if (this.debug && matchesWaist && matchesInseam) {
                  console.log('[ProductGrouping] Checking cached variant for availability (first check):', {
                    variantId: v.id,
                    available: v.available,
                    isAvailable: isAvailable,
                    variantWaist: v.option2,
                    variantInseam: v.option3,
                    selectedWaist: selectedWaist,
                    selectedInseam: selectedInseam,
                    normalized: {
                      variantWaist: normalizedVariantWaist,
                      variantInseam: normalizedVariantInseam,
                      selectedWaist: normalizedSelectedWaist,
                      selectedInseam: normalizedSelectedInseam
                    }
                  });
                }
                
                return isAvailable && matchesWaist && matchesInseam;
              }
            );
            
            if (firstAvailableVariantFromCache && this.debug) {
              console.log(
                "[ProductGrouping] Found available variant from cache matching both waist and inseam:",
                {
                  id: firstAvailableVariantFromCache.id,
                  option2: firstAvailableVariantFromCache.option2,
                  option3: firstAvailableVariantFromCache.option3,
                  available: firstAvailableVariantFromCache.available
                }
              );
            } else if (this.debug && productData.product?.variants) {
              // Log why no variant was found - show sample variants for debugging
              const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
              const normalizedSelectedWaist = normalizeValue(selectedWaist);
              const normalizedSelectedInseam = normalizeValue(selectedInseam);
              
              const matchingVariants = productData.product.variants.filter(v => {
                const normalizedVariantWaist = normalizeValue(v.option2);
                const normalizedVariantInseam = normalizeValue(v.option3);
                return normalizedVariantWaist === normalizedSelectedWaist && normalizedVariantInseam === normalizedSelectedInseam;
              });
              
              // Show sample of variants with option2 matching waist
              const waistMatches = productData.product.variants.filter(v => {
                const normalizedVariantWaist = normalizeValue(v.option2);
                return normalizedVariantWaist === normalizedSelectedWaist;
              }).slice(0, 5);
              
              console.log('[ProductGrouping] Cached check - no available variant found:', {
                selectedWaist,
                selectedInseam,
                normalizedSelectedWaist,
                normalizedSelectedInseam,
                totalVariants: productData.product.variants.length,
                matchingVariantsCount: matchingVariants.length,
                matchingVariants: matchingVariants.map(v => ({
                  id: v.id,
                  option2: v.option2,
                  option3: v.option3,
                  available: v.available,
                  normalized: {
                    option2: normalizeValue(v.option2),
                    option3: normalizeValue(v.option3)
                  }
                })),
                sampleWaistMatches: waistMatches.map(v => ({
                  id: v.id,
                  option2: v.option2,
                  option3: v.option3,
                  normalized: {
                    option2: normalizeValue(v.option2),
                    option3: normalizeValue(v.option3)
                  }
                }))
              });
            }
          }
          
          // If we found an available variant in cache, use it immediately
          if (firstAvailableVariantFromCache) {
            this.currentVariantId = firstAvailableVariantFromCache.id.toString();
            
            // Update variant input
            const variantInput = document.querySelector('.product-info input[name="id"]');
            if (variantInput) {
              variantInput.value = this.currentVariantId;
            }
            
            // Update current options
            if (firstAvailableVariantFromCache.option2) {
              this.currentOptions.option2 = firstAvailableVariantFromCache.option2;
            }
            if (firstAvailableVariantFromCache.option3) {
              this.currentOptions.option3 = firstAvailableVariantFromCache.option3;
            }
            
            // Update visual state
            this.updateVariantClass();
            this._updateURLWithVariant(this.currentVariantId);
            
            // Update button state immediately with cached availability
            setTimeout(() => {
              const form = document.querySelector(".product-info .shopify-product-form");
              const soldOut = document.querySelector(".product-info sold-out-badge");
              if (form) {
                this._updateButtonStateAfterColorChange(
                  form,
                  soldOut,
                  true, // Use cached availability
                  firstAvailableVariantFromCache
                );
              }
            }, 100);
            
            // Verify with real-time check in background (but don't downgrade)
            const bgVerifyVariantId = this.currentVariantId;
            this._fetchVariantAvailability(bgVerifyVariantId).then((realTimeAvailable) => {
              if (String(this.currentVariantId) !== String(bgVerifyVariantId)) {
                return;
              }
              if (this.debug) {
                console.log("[ProductGrouping] Real-time check for cached variant:", realTimeAvailable);
              }
              // Only update if real-time confirms availability, don't downgrade
              if (realTimeAvailable === true) {
                const form = document.querySelector(".product-info .shopify-product-form");
                const soldOut = document.querySelector(".product-info sold-out-badge");
                if (form) {
                  this._updateButtonStateAfterColorChange(
                    form,
                    soldOut,
                    true,
                    firstAvailableVariantFromCache
                  );
                }
              }
            }).catch(() => {
              // On error, trust cached data
            });
            
            return; // Exit early, we've handled it
          }

          // Declare these in broader scope so they're accessible later
          let firstAvailableButton = null;
          let firstAvailableVariant = null;

          // CRITICAL: Fetch real-time availability for the selected variant after color change
          if (this.currentVariantId) {
            const colorChangeAvailabilityVariantId = this.currentVariantId;
            this._fetchVariantAvailability(colorChangeAvailabilityVariantId)
              .then((realTimeAvailable) => {
                if (String(this.currentVariantId) !== String(colorChangeAvailabilityVariantId)) {
                  return;
                }
                if (realTimeAvailable == null) {
                  return;
                }

                if (this.debug) {
                  console.log(
                    "[ProductGrouping] Color change - real-time availability:",
                    {
                      variantId: colorChangeAvailabilityVariantId,
                      realTimeAvailable,
                    }
                  );
                }

                // If the preserved size variant is available, update button to show "Add to Bag"
                if (realTimeAvailable === true) {
                  // Variant is available - update button state
                  setTimeout(() => {
                    if (String(this.currentVariantId) !== String(colorChangeAvailabilityVariantId)) {
                      return;
                    }
                    const form = document.querySelector(".product-info .shopify-product-form");
                    const soldOut = document.querySelector(".product-info sold-out-badge");
                    if (form) {
                      // Get current variant for price display
                      const currentVariant = productData.product?.variants?.find(
                        v => v.id.toString() === colorChangeAvailabilityVariantId?.toString()
                      ) || this.productGroupingData[this.currentProductId]?.variants?.find(
                        v => v.id.toString() === colorChangeAvailabilityVariantId?.toString()
                      );
                      
                      if (currentVariant) {
                        this._updateButtonStateAfterColorChange(
                          form,
                          soldOut,
                          true, // Available
                          currentVariant
                        );
                      }
                    }
                  }, 100);
                  return; // Exit early, we've handled it
                }
                
                // If the preserved size variant is not available, try to find variant matching BOTH waist and inseam
                if (!realTimeAvailable) {
                  
                  if (this.debug) {
                    console.log(
                      "[ProductGrouping] Looking for available variant matching BOTH waist and inseam:",
                      {
                        selectedWaist,
                        selectedInseam
                      }
                    );
                  }

                  // Only find variants that match BOTH waist and inseam - no fallbacks
                  const variantButtons = document.querySelectorAll('button[js-update-variant]');
                  
                  if (variantButtons.length > 0 && selectedWaist && selectedInseam) {
                    // Use normalization to handle quotes and whitespace differences
                    const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
                    const normalizedSelectedWaist = normalizeValue(selectedWaist);
                    const normalizedSelectedInseam = normalizeValue(selectedInseam);
                    
                    // Must match BOTH waist and inseam (normalized)
                    firstAvailableButton = Array.from(variantButtons).find(btn => {
                      const available = btn.dataset.available;
                      const normalizedButtonWaist = normalizeValue(btn.dataset.option2);
                      const normalizedButtonInseam = normalizeValue(btn.dataset.option3);
                      const matchesWaist = normalizedButtonWaist === normalizedSelectedWaist;
                      const matchesInseam = normalizedButtonInseam === normalizedSelectedInseam;
                      
                      // Check availability - handle string 'true', boolean true, or '1'
                      const isAvailable = available === 'true' || available === true || available === '1' || available === 1;
                      
                      if (this.debug && matchesWaist && matchesInseam) {
                        console.log('[ProductGrouping] Checking button availability:', {
                          variantId: btn.dataset.variantId,
                          available: available,
                          availableType: typeof available,
                          isAvailable: isAvailable,
                          matchesWaist,
                          matchesInseam
                        });
                      }
                      
                      return isAvailable && matchesWaist && matchesInseam;
                    });
                    
                    if (this.debug) {
                      if (firstAvailableButton) {
                        console.log(
                          "[ProductGrouping] Found variant matching both waist and inseam:",
                          firstAvailableButton.dataset.variantId,
                          "Waist:",
                          firstAvailableButton.dataset.option2,
                          "Inseam:",
                          firstAvailableButton.dataset.option3
                        );
                      } else {
                        console.log(
                          "[ProductGrouping] No available variant found matching both waist and inseam:",
                          "Waist:",
                          selectedWaist,
                          "Inseam:",
                          selectedInseam,
                          "- Will show Join Waitlist"
                        );
                      }
                    }
                  } else if (this.debug) {
                    console.log(
                      "[ProductGrouping] Cannot search for matching variant - missing waist or inseam:",
                      {
                        hasWaist: !!selectedWaist,
                        hasInseam: !!selectedInseam,
                        hasButtons: variantButtons.length > 0
                      }
                    );
                  }

                  // Fallback: try from cached product data (must also match both waist and inseam)
                  if (!firstAvailableButton && productData.product?.variants && selectedWaist && selectedInseam) {
                    if (this.debug) {
                      console.log(
                        "[ProductGrouping] Checking cached product data for variants matching both waist and inseam. Total variants:",
                        productData.product.variants.length
                      );
                    }
                    
                    // Find first available variant that matches BOTH waist and inseam
                    // Use normalization to handle quotes and whitespace differences
                    const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '').toLowerCase() : '';
                    const normalizedSelectedWaist = normalizeValue(selectedWaist);
                    const normalizedSelectedInseam = normalizeValue(selectedInseam);
                    
                    firstAvailableVariant = productData.product.variants.find(
                      (v) => {
                        // Normalize variant values for comparison
                        const normalizedVariantWaist = normalizeValue(v.option2);
                        const normalizedVariantInseam = normalizeValue(v.option3);
                        
                        // Must match both waist and inseam (normalized) AND be available
                        const matchesWaist = normalizedVariantWaist === normalizedSelectedWaist;
                        const matchesInseam = normalizedVariantInseam === normalizedSelectedInseam;
                        // Check availability - handle boolean true, string 'true', or number 1
                        const isAvailable = v.available === true || v.available === "true" || v.available === 1 || v.available === "1";
                        
                        if (this.debug && matchesWaist && matchesInseam) {
                          console.log('[ProductGrouping] Checking variant availability:', {
                            variantId: v.id,
                            available: v.available,
                            availableType: typeof v.available,
                            isAvailable: isAvailable,
                            matchesWaist,
                            matchesInseam,
                            variantWaist: v.option2,
                            variantInseam: v.option3,
                            selectedWaist: selectedWaist,
                            selectedInseam: selectedInseam,
                            normalized: {
                              variantWaist: normalizedVariantWaist,
                              variantInseam: normalizedVariantInseam,
                              selectedWaist: normalizedSelectedWaist,
                              selectedInseam: normalizedSelectedInseam
                            }
                          });
                        }
                        
                        return isAvailable && matchesWaist && matchesInseam;
                      }
                    );

                    if (this.debug) {
                      if (firstAvailableVariant) {
                        console.log(
                          "[ProductGrouping] Found variant from cached data matching both waist and inseam:",
                          {
                            id: firstAvailableVariant.id,
                            available: firstAvailableVariant.available,
                            option2: firstAvailableVariant.option2,
                            option3: firstAvailableVariant.option3
                          }
                        );
                      } else {
                        console.log(
                          "[ProductGrouping] No available variant in cached data matching both waist and inseam"
                        );
                      }
                    }
                  }

                  // Use button data if found, otherwise use variant data
                  if (firstAvailableButton) {
                    const variantId = firstAvailableButton.dataset.variantId;
                    const variantPrice = firstAvailableButton.dataset.variantPrice;
                    const option2 = firstAvailableButton.dataset.option2;
                    const option3 = firstAvailableButton.dataset.option3;
                    
                    if (this.debug) {
                      console.log(
                        "[ProductGrouping] Found available variant from DOM button:",
                        variantId,
                        "Available:",
                        firstAvailableButton.dataset.available
                      );
                    }

                    // Switch to the first available variant
                    this.currentVariantId = variantId;
                    
                    // Update variant input
                    const variantInput = document.querySelector(
                      '.product-info input[name="id"]'
                    );
                    if (variantInput) {
                      variantInput.value = this.currentVariantId;
                    }

                    // Update current options to match the new variant
                    if (option2) {
                      this.currentOptions.option2 = option2;
                    }
                    if (option3) {
                      this.currentOptions.option3 = option3;
                    }

                    // Update visual state of size/option buttons
                    this.updateVariantClass();

                    // Update URL with new variant
                    this._updateURLWithVariant(this.currentVariantId);

                    // Create variant object for button update
                    const variantForUpdate = {
                      id: variantId,
                      price: variantPrice ? (typeof variantPrice === 'string' ? Math.round(parseFloat(variantPrice) * 100) : variantPrice) : null,
                      option2: option2,
                      option3: option3,
                      available: true
                    };

                    // Use cached availability (true) since we found a variant with available === true
                    // The real-time check might fail or be delayed, so trust the cached data
                    // Update button visibility immediately with the cached availability
                    // Use a small delay to ensure DOM is fully updated
                    setTimeout(() => {
                      // Re-query form and elements to ensure we have fresh references
                      const form = document.querySelector(
                        ".product-info .shopify-product-form"
                      );
                      const soldOut = document.querySelector(
                        ".product-info sold-out-badge"
                      );
                      if (form) {
                        this._updateButtonStateAfterColorChange(
                          form,
                          soldOut,
                          true, // Use cached availability - we know this variant is available
                          variantForUpdate
                        );
                      } else if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Form not found when trying to update button state"
                        );
                      }
                    }, 100);
                    
                    // Optionally verify with real-time check in the background (but don't block on it)
                    // We trust the cached data since we found a variant with available === true
                    // Only update if real-time check confirms availability (don't downgrade to unavailable)
                    const postSwitchVerifyIdA = this.currentVariantId;
                    this._fetchVariantAvailability(postSwitchVerifyIdA).then(
                      (newRealTimeAvailable) => {
                        if (String(this.currentVariantId) !== String(postSwitchVerifyIdA)) {
                          return;
                        }
                        if (this.debug) {
                          console.log(
                            "[ProductGrouping] Real-time availability check result:",
                            newRealTimeAvailable,
                            "for variant:",
                            postSwitchVerifyIdA
                          );
                        }
                        // Only update button state if real-time check confirms it's available
                        // Don't downgrade to unavailable if real-time check fails or returns false
                        // because we trust the cached data that says it's available
                        if (newRealTimeAvailable === true && this.currentVariantId === variantId) {
                          // Real-time check confirms availability - ensure button is still showing "Add to Bag"
                          const form = document.querySelector(
                            ".product-info .shopify-product-form"
                          );
                          const soldOut = document.querySelector(
                            ".product-info sold-out-badge"
                          );
                          if (form) {
                            this._updateButtonStateAfterColorChange(
                              form,
                              soldOut,
                              true,
                              variantForUpdate
                            );
                          }
                        } else if (newRealTimeAvailable === false && this.debug) {
                          console.warn(
                            "[ProductGrouping] Real-time check shows variant unavailable, but cached data says available. Keeping 'Add to Bag' button based on cached data."
                          );
                        }
                      }
                    ).catch((error) => {
                      if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Real-time availability check failed, using cached data:",
                          error
                        );
                      }
                      // On error, trust the cached data - don't change button state
                    });
                    
                    // Throw a special error to prevent the rest of the code from running
                    const switchError = new Error('Variant switched, button update handled');
                    switchError.isVariantSwitch = true;
                    throw switchError;
                  } else if (firstAvailableVariant) {
                    if (this.debug) {
                      console.log(
                        "[ProductGrouping] Preserved size unavailable, switching to first available variant:",
                        firstAvailableVariant.id,
                        "Available:",
                        firstAvailableVariant.available
                      );
                    }

                    // Switch to the first available variant
                    this.currentVariantId = firstAvailableVariant.id.toString();
                    
                    // Update variant input
                    const variantInput = document.querySelector(
                      '.product-info input[name="id"]'
                    );
                    if (variantInput) {
                      variantInput.value = this.currentVariantId;
                    }

                    // Update current options to match the new variant
                    if (firstAvailableVariant.option2) {
                      this.currentOptions.option2 = firstAvailableVariant.option2;
                    }
                    if (firstAvailableVariant.option3) {
                      this.currentOptions.option3 = firstAvailableVariant.option3;
                    }

                    // Update visual state of size/option buttons
                    this.updateVariantClass();

                    // Update URL with new variant
                    this._updateURLWithVariant(this.currentVariantId);

                    // Use cached availability (true) since we found a variant with available === true
                    // The real-time check might fail or be delayed, so trust the cached data
                    // Update button visibility immediately with the cached availability
                    // Use a small delay to ensure DOM is fully updated
                    setTimeout(() => {
                      // Re-query form and elements to ensure we have fresh references
                      const form = document.querySelector(
                        ".product-info .shopify-product-form"
                      );
                      const soldOut = document.querySelector(
                        ".product-info sold-out-badge"
                      );
                      if (form) {
                        this._updateButtonStateAfterColorChange(
                          form,
                          soldOut,
                          true, // Use cached availability - we know this variant is available
                          firstAvailableVariant
                        );
                      } else if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Form not found when trying to update button state"
                        );
                      }
                    }, 100);
                    
                    // Optionally verify with real-time check in the background (but don't block on it)
                    // We trust the cached data since we found a variant with available === true
                    // Only update if real-time check confirms availability (don't downgrade to unavailable)
                    const postSwitchVerifyIdB = this.currentVariantId;
                    this._fetchVariantAvailability(postSwitchVerifyIdB).then(
                      (newRealTimeAvailable) => {
                        if (String(this.currentVariantId) !== String(postSwitchVerifyIdB)) {
                          return;
                        }
                        if (this.debug) {
                          console.log(
                            "[ProductGrouping] Real-time availability check result:",
                            newRealTimeAvailable,
                            "for variant:",
                            postSwitchVerifyIdB
                          );
                        }
                        // Only update button state if real-time check confirms it's available
                        // Don't downgrade to unavailable if real-time check fails or returns false
                        // because we trust the cached data that says it's available
                        if (newRealTimeAvailable === true && this.currentVariantId === firstAvailableVariant.id.toString()) {
                          // Real-time check confirms availability - ensure button is still showing "Add to Bag"
                          this._updateButtonStateAfterColorChange(
                            newAddToCartForm,
                            newSoldOutElement,
                            true,
                            firstAvailableVariant
                          );
                        } else if (newRealTimeAvailable === false && this.debug) {
                          console.warn(
                            "[ProductGrouping] Real-time check shows variant unavailable, but cached data says available. Keeping 'Add to Bag' button based on cached data."
                          );
                        }
                      }
                    ).catch((error) => {
                      if (this.debug) {
                        console.warn(
                          "[ProductGrouping] Real-time availability check failed, using cached data:",
                          error
                        );
                      }
                      // On error, trust the cached data - don't change button state
                    });
                    
                    // Throw a special error to prevent the rest of the code from running
                    const switchError = new Error('Variant switched, button update handled');
                    switchError.isVariantSwitch = true;
                    throw switchError;
                  }
                }

                // If we didn't switch variants, check if we should show Join Waitlist
                // This happens when no available variant was found matching both waist and inseam
                if (!firstAvailableButton && !firstAvailableVariant && selectedWaist && selectedInseam) {
                  // No available variant found - explicitly show Join Waitlist
                  if (this.debug) {
                    console.log(
                      "[ProductGrouping] No available variant found - showing Join Waitlist for:",
                      { waist: selectedWaist, inseam: selectedInseam }
                    );
                  }
                  
                  setTimeout(() => {
                    const form = document.querySelector(".product-info .shopify-product-form");
                    const soldOut = document.querySelector(".product-info sold-out-badge");
                    if (form) {
                      // Get current variant for price display
                      const currentVariant = productData.product?.variants?.find(
                        v => v.id.toString() === this.currentVariantId?.toString()
                      );
                      
                      this._updateButtonStateAfterColorChange(
                        form,
                        soldOut,
                        false, // Not available
                        currentVariant || { id: this.currentVariantId }
                      );
                    }
                  }, 100);
                  
                  return; // Exit early, we've handled it
                }
                
                // If we didn't switch variants, proceed with normal button update
                // Re-query buttons after DOM update
                const buyButtonsEl =
                  newAddToCartForm?.querySelector("buy-buttons");
                const allButtons = buyButtonsEl
                  ? Array.from(buyButtonsEl.querySelectorAll("button"))
                  : [];

                const newNotifyMeBtn = allButtons.find((btn) =>
                  btn.hasAttribute("data-bis-trigger")
                );
                const newAddToCartButton =
                  this._buyButtonsSubmitButton(allButtons);
                // Handle both button structures: with div wrapper (quick-add) and without (PDP)
                const newAddToCartButtonText =
                  newAddToCartButton?.querySelector("div") ||
                  newAddToCartButton;

                if (newAddToCartButton && newNotifyMeBtn) {
                  const ctaVariantId = colorChangeAvailabilityVariantId;
                  const ctaVariant =
                    productData.product?.variants?.find(
                      (v) => v.id.toString() === ctaVariantId?.toString()
                    ) ||
                    this.productGroupingData[this.currentProductId]?.variants?.find(
                      (v) => v.id.toString() === ctaVariantId?.toString()
                    );
                  let variantPriceForCTA = null;
                  if (ctaVariant?.price) {
                    const priceInCents =
                      typeof ctaVariant.price === "string"
                        ? parseInt(ctaVariant.price, 10)
                        : ctaVariant.price;
                    if (!Number.isNaN(priceInCents) && priceInCents > 0) {
                      const dollars = priceInCents / 100;
                      variantPriceForCTA =
                        dollars % 1 === 0
                          ? dollars.toString()
                          : dollars.toFixed(2);
                    }
                  }
                  if (!variantPriceForCTA) {
                    const activeBtn = document.querySelector(
                      'button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]'
                    );
                    if (activeBtn?.dataset?.variantPrice) {
                      variantPriceForCTA = activeBtn.dataset.variantPrice;
                    }
                  }

                  // Price will be read directly from sale-price element in _updateButtonVisibility
                  // When color changes, the HTML is replaced, so we need to wait for the new price-list to be ready
                  // Use requestAnimationFrame to ensure DOM is updated, then try multiple times if needed
                  const updateButton = () => {
                    this._updateButtonVisibility(
                      newAddToCartButton,
                      newNotifyMeBtn,
                      newAddToCartButtonText,
                      newSoldOutElement,
                      realTimeAvailable
                    );
                    
                    // Check if button text was updated with a price
                    const buttonText = newAddToCartButtonText?.textContent || '';
                    const hasPrice = buttonText.includes('$') && /\$\d/.test(buttonText);
                    
                    // If no price in button text, retry after a short delay (price-list might still be updating)
                    if (!hasPrice && realTimeAvailable) {
                      setTimeout(() => {
                        this._updateButtonVisibility(
                          newAddToCartButton,
                          newNotifyMeBtn,
                          newAddToCartButtonText,
                          newSoldOutElement,
                          realTimeAvailable
                        );
                      }, 150);
                    }
                  };
                  
                  // Wait for DOM to update after color change (double RAF to ensure all updates are done)
                  requestAnimationFrame(() => {
                    requestAnimationFrame(updateButton);
                  });
                  
                  // Update quick add CTA with correct price
                  if (variantPriceForCTA) {
                    this.updateQuickAddCTA(realTimeAvailable, this.currentVariantId, variantPriceForCTA);
                  }

                  // CRITICAL: Notify AMP Back in Stock app about the new variant after color change
                  // This ensures the BIS modal shows the correct size options for the newly selected color
                  this._notifyAmpBackInStock({
                    id: this.currentVariantId,
                    available: realTimeAvailable,
                  });

                  // Dispatch variant:change event for other systems
                  // Get variant price for the event
                  const currentVariantForEvent = this._getCurrentVariant?.();
                  let variantPriceForEvent = null;
                  if (currentVariantForEvent && typeof currentVariantForEvent.price !== 'undefined') {
                    variantPriceForEvent = currentVariantForEvent.price;
                  } else {
                    // Try to get price from active button
                    const activeButton = document.querySelector('button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]');
                    if (activeButton && activeButton.dataset.variantPrice) {
                      const priceStr = activeButton.dataset.variantPrice;
                      const priceValue = parseFloat(priceStr);
                      if (!Number.isNaN(priceValue)) {
                        variantPriceForEvent = Math.round(priceValue * 100); // Convert to cents
                      }
                    }
                  }
                  
                  document.dispatchEvent(
                    new CustomEvent("variant:change", {
                      bubbles: true,
                      detail: {
                        variant: {
                          id: this.currentVariantId,
                          available: realTimeAvailable,
                          price: variantPriceForEvent,
                        },
                        formId: newAddToCartForm?.id,
                      },
                    })
                  );
                } else {
                  console.warn(
                    "[ProductGrouping] Buttons not found after color change, retrying...",
                    {
                      hasAddToCart: !!newAddToCartButton,
                      hasWaitlist: !!newNotifyMeBtn,
                      totalButtons: allButtons.length,
                    }
                  );
                }
              })
              .catch((error) => {
                // Ignore the special error that indicates we've already handled the button update
                if (error.isVariantSwitch) {
                  if (this.debug) {
                    console.log(
                      "[ProductGrouping] Variant switched, button update already handled"
                    );
                  }
                  return;
                }
                console.error(
                  "[ProductGrouping] Failed to fetch availability after color change:",
                  error
                );
              });
          }
        });

        this._finishProductSwap({ newMainProduct, newDocument, newProduct, url, params, loadedIds });
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error(
          "[ProductGrouping] Error fetching new product data:",
          error
        );

        // User-friendly fallback: reload the page with the correct product
        if (this.debug) {
          console.log(
            "[ProductGrouping] Attempting full page navigation to:",
            newProduct
          );
        }
        // Only navigate if we have a valid product handle
        if (newProduct && typeof newProduct === "string") {
          window.location.href = `/products/${newProduct}`;
        }
      } finally {
        if (requestId === this.productRequestId) {
          this.parentComponent.classList.remove('is-loading');
          this.productRequestController = null;
        }
      }
    }
  }

  /** Steps shared by every product swap once the new product's markup is in place */
  _finishProductSwap({ newMainProduct, newDocument, newProduct, url, params, loadedIds }) {
    const scripts = Array.from(
      newMainProduct?.querySelectorAll("script") || []
    ).filter((script) => !script.hasAttribute("data-product-group"));
    if (!this._executedInlineScripts) {
      this._executedInlineScripts = new Set();
    }
    scripts.forEach((oldScript) => {
      const content = oldScript.textContent?.trim();
      if (!content || this._executedInlineScripts.has(content)) return;
      this._executedInlineScripts.add(content);
      try {
        // Avoid document.body.appendChild — Yottaa patches it and throws on inline scripts
        new Function(content)();
      } catch (error) {
        if (this.debug) {
          console.warn("[ProductGrouping] Inline script exec failed:", error);
        }
      }
    });

    window.dispatchEvent(new Event("productGroupingInjected"));

    if (!this.isQuickView) {
      params.delete("view");
      params.delete("section_id");
      params.delete("no-fade-in");
      if (loadedIds) {
        params.delete("product-group-ids");
      }
      url.search = params.toString();
      window.history.replaceState({}, "", url.toString());

      const optionColor = document.querySelector(
        `button[data-product-handle="${newProduct}"]`
      );
      const currentQuickAdd = document.querySelector("#quick-add--sticky");
      const newQuickAdd = newDocument.querySelector("#quick-add--sticky");
      if (currentQuickAdd && newQuickAdd) {
        currentQuickAdd.replaceWith(newQuickAdd);
      }

      setTimeout(() => {
        if (!this.isQuickView) {
          window.yotpoWidgetsContainer?.initWidgets();
        }
        if (optionColor) {
          optionColor.focus();
        }
        const isMobile = window.matchMedia("(max-width: 768px)").matches;
        if (isMobile) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 300);
    }

    this.currentProductHandle = newProduct;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._persistSubOptionGroupingPreferenceFromDom();
      });
    });
  }

  _updateSizeGuide() {
    const productTags = this.getAttribute("data-product-tags") || "";
    const sizeGuideHandle = this._extractSizeGuideTag(productTags);
    if (!sizeGuideHandle) return;

    // The chart already on the page is the one this product wants — the common
    // case on every cart drawer re-render — so there is nothing to fetch.
    const target = document.querySelector("#size-chart-content");
    if (!target || target.dataset.sizeGuideHandle === sizeGuideHandle) return;

    fetchSizeGuideContent(sizeGuideHandle)
      .then(content => {
        if (content === null) return;
        const current = document.querySelector("#size-chart-content");
        if (!current || current.dataset.sizeGuideHandle === sizeGuideHandle) return;
        current.innerHTML = content;
        current.dataset.sizeGuideHandle = sizeGuideHandle;
      })
      .catch(error => console.error("Error loading size guide:", error));
  }

  _extractSizeGuideTag(tags) {
    const match = tags.split(",").find(tag => tag.includes("size-guide:"));
    return match ? match.split(":")[1] : "";
  }

  /**
   * Get the currently selected size from active size buttons
   */
  _getCurrentSelectedSize() {
    // Look for active size buttons in the product grouping component
    const activeSizeButton = this.querySelector('.product-grouping__options.is-size .product-option.is-active');
    if (activeSizeButton) {
      const sizeText = activeSizeButton.querySelector('span')?.textContent?.trim();
      if (this.debug) console.log('Found active size button:', sizeText);
      return sizeText;
    }

    // Fallback: check variant picker size buttons  
    const activeVariantButton = this.querySelector('.variant-picker__option-values .product-option.is-active');
    if (activeVariantButton) {
      const sizeText = activeVariantButton.querySelector('span')?.textContent?.trim();
      if (this.debug) console.log('Found active variant button:', sizeText);
      return sizeText;
    }

    // Last resort: get from current variant data
    const currentVariant = this._getCurrentVariant();
    if (currentVariant) {
      // Size could be in option2 or option3 depending on product structure
      const possibleSize = currentVariant.option2 || currentVariant.option3;
      if (possibleSize && possibleSize !== 'Default Title') {
        if (this.debug) console.log('Found size from current variant:', possibleSize);
        return possibleSize;
      }
    }

    if (this.debug) console.log('No current size found');
    return null;
  }

  /**
   * Get current variant data from the variant picker or product grouping
   */
  _getCurrentVariant() {
    try {
      // First, try variant-picker (for standard products)
      const variantScript = document.querySelector('variant-picker script[data-variant]');
      if (variantScript) {
        const variant = JSON.parse(variantScript.textContent || '{}');
        if (variant && variant.id) {
          if (this.debug) console.log('Found variant from variant-picker:', variant);
          return variant;
        }
      }

      // Fallback: For product-grouping, reconstruct from current state
      if (this.currentVariantId && this.productGroupingData && this.currentProductId) {
        const productData = this.productGroupingData[this.currentProductId];
        if (productData && productData.variants) {
          const currentVariant = productData.variants.find(
            v => v.id.toString() === this.currentVariantId.toString()
          );
          if (currentVariant) {
            if (this.debug) console.log('Found variant from product-grouping data:', currentVariant);
            return currentVariant;
          }
        }
      }

      // Last resort: Use tracked options to build a partial variant object
      if (this.currentOptions.option2 || this.currentOptions.option3) {
        const partialVariant = {
          id: this.currentVariantId,
          option2: this.currentOptions.option2,
          option3: this.currentOptions.option3
        };
        
        // Try to get price from product grouping data
        if (this.currentVariantId && this.productGroupingData && this.currentProductId) {
          const productData = this.productGroupingData[this.currentProductId];
          if (productData && productData.variants) {
            const variantFromData = productData.variants.find(
              v => v.id.toString() === this.currentVariantId.toString()
            );
            if (variantFromData && variantFromData.price) {
              partialVariant.price = variantFromData.price;
              if (this.debug) {
                console.log('Got price from product grouping data for partial variant:', partialVariant.price);
              }
            }
          }
        }
        
        // If still no price, try to get from active button
        if (!partialVariant.price) {
          const activeButton = document.querySelector('button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]');
          if (activeButton && activeButton.dataset.variantPrice) {
            const priceStr = activeButton.dataset.variantPrice;
            const priceValue = parseFloat(priceStr);
            if (!Number.isNaN(priceValue)) {
              // Price is in dollars, convert to cents
              partialVariant.price = Math.round(priceValue * 100);
              if (this.debug) {
                console.log('Got price from active button for partial variant:', partialVariant.price);
              }
            }
          }
        }
        
        // If still no price, try variant picker script
        if (!partialVariant.price) {
          const variantScript = document.querySelector('variant-picker script[data-variant]');
          if (variantScript) {
            try {
              const variantFromScript = JSON.parse(variantScript.textContent || '{}');
              if (variantFromScript && variantFromScript.id && variantFromScript.id.toString() === this.currentVariantId.toString() && variantFromScript.price) {
                partialVariant.price = variantFromScript.price;
                if (this.debug) {
                  console.log('Got price from variant picker script for partial variant:', partialVariant.price);
                }
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
        
        if (this.debug) console.log('Built variant from currentOptions:', partialVariant);
        return partialVariant;
      }
    } catch (error) {
      if (this.debug) console.warn('Could not parse current variant:', error);
    }

    if (this.debug) console.warn('No current variant found from any source');
    return null;
  }

  /**
   * Find a variant that matches the target option2 and/or option3 values
   * Works for both regular products (single size) and pants (waist + inseam)
   */
  _findMatchingVariant(variants, targetOption2, targetOption3) {
    if (!variants || (!targetOption2 && !targetOption3)) {
      if (this.debug) console.log('_findMatchingVariant: No variants or target options provided');
      return null;
    }

    // Normalize "Default Title" to null for easier comparison
    const normalizedOption2 = targetOption2 === 'Default Title' ? null : targetOption2;
    const normalizedOption3 = targetOption3 === 'Default Title' ? null : targetOption3;

    if (this.debug) {
      console.log('_findMatchingVariant searching for:', {
        option2: normalizedOption2,
        option3: normalizedOption3,
        totalVariants: variants.length
      });
      // Log a sample of variants to see their structure
      console.log('Sample variants:', variants.slice(0, 3).map(v => ({
        id: v.id,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3
      })));
    }

    const normalizeValue = (val) =>
      val ? val.toString().replace(/["'"\s]/g, "").toLowerCase() : "";

    try {
      for (const variant of variants) {
        // Match both option2 and option3 if both are meaningful values
        if (normalizedOption2 && normalizedOption3) {
          const variantOption2Normalized = normalizeValue(variant.option2);
          const targetOption2Normalized = normalizeValue(normalizedOption2);
          const variantOption3Normalized = normalizeValue(variant.option3);
          const targetOption3Normalized = normalizeValue(normalizedOption3);

          const option2Match = variantOption2Normalized === targetOption2Normalized;
          const option3Match = variantOption3Normalized === targetOption3Normalized;

          if (this.debug && (option2Match || option3Match)) {
            console.log('Checking variant:', variant.id, {
              variantOption2: variant.option2,
              variantOption2Normalized,
              targetOption2: normalizedOption2,
              targetOption2Normalized,
              option2Match,
              variantOption3: variant.option3,
              variantOption3Normalized,
              targetOption3: normalizedOption3,
              targetOption3Normalized,
              option3Match
            });
          }

          if (option2Match && option3Match) {
            if (this.debug) console.log('✓ Found exact match (option2 + option3):', variant.id, variant);
            return variant.id.toString();
          }
        }
        // Match just option2 if option3 is not meaningful (includes standard ↔ tall: L ↔ LT, M ↔ MT, …)
        else if (normalizedOption2 && !normalizedOption3) {
          const v2 = variant.option2;
          if (!v2) continue;
          if (v2 === normalizedOption2) {
            if (this.debug) console.log('✓ Found option2 exact match:', variant.id, variant);
            return variant.id.toString();
          }
          const tNorm = normalizeValue(normalizedOption2);
          const vNorm = normalizeValue(v2);
          const stripTrailingT =
            tNorm.length >= 2 && tNorm.endsWith("t")
              ? tNorm.slice(0, -1)
              : null;
          const candidates = new Set([tNorm, `${tNorm}t`]);
          if (stripTrailingT) candidates.add(stripTrailingT);
          if (candidates.has(vNorm)) {
            if (this.debug) console.log('✓ Found option2 normalized / tall match:', variant.id, variant);
            return variant.id.toString();
          }
        }
      }
    } catch (error) {
      if (this.debug) console.warn('Error finding matching variant:', error);
    }

    if (this.debug) console.log('No matching variant found for the target options');
    return null;
  }

  /**
   * Check if an option represents a size selection
   */
  _isOptionSize(optionIndex, value) {
    // Look for size-related indicators
    const sizeIndicators = [
      "xs",
      "xsmall",
      "s",
      "small",
      "m",
      "medium",
      "l",
      "large",
      "xl",
      "xlarge",
      "xxl",
      "2xl",
      "3xl",
      "28",
      "29",
      "30",
      "31",
      "32",
      "33",
      "34",
      "35",
      "36",
      "38",
      "40",
      "42",
      "44",
      "46",
      "48",
      "50",
    ];

    const valueLower = value.toLowerCase().replace(/[^a-z0-9]/g, "");

    // Check if the value looks like a size
    const looksLikeSize = sizeIndicators.some((indicator) =>
      valueLower.includes(indicator)
    );

    // Also check if this option row is marked as size
    const optionRow = this.querySelector(`[data-options-row="${optionIndex}"]`);
    const isMarkedAsSize = optionRow?.classList.contains("is-size");

    return looksLikeSize || isMarkedAsSize;
  }

  /**
   * Get the size that should be preserved during color changes
   * Prioritizes user's preferred size if they've made a selection
   */
  _getSizeToPreserve() {
    // If user has explicitly selected a size, always try to preserve their preference
    if (this.hasUserSelectedSize && this.preferredSize) {
      return this.preferredSize;
    }

    // Otherwise fall back to current selection
    const currentSize = this._getCurrentSelectedSize();

    // If we found a current size and haven't captured a preference yet, capture it
    if (currentSize && !this.hasUserSelectedSize) {
      this.preferredSize = currentSize;
      this.hasUserSelectedSize = true;
    }
    
    return currentSize;
  }

  /**
   * Find the first in-stock size variant when the default is unavailable
   * and the shopper has not picked a size yet.
   */
  async _findFirstAvailableVariantAsync() {
    const isAvailableFlag = (value) =>
      value === "true" || value === true || value === "1" || value === 1;

    const variantButtons = this.querySelectorAll("button[js-update-variant]");
    for (const button of variantButtons) {
      if (isAvailableFlag(button.dataset.available)) {
        return { button };
      }
    }

    const productData = this.productGroupingData?.[this.currentProductId];
    const candidates = productData?.variants?.filter((variant) =>
      isAvailableFlag(variant.available)
    );

    if (!candidates?.length) {
      return null;
    }

    for (const candidate of candidates) {
      const button = this.querySelector(
        `button[js-update-variant][data-variant-id="${candidate.id}"]`
      );
      if (!button) continue;

      try {
        const available = await this._fetchVariantAvailability(candidate.id);
        if (available) {
          return { button };
        }
      } catch (error) {
        if (this.debug) {
          console.warn(
            "[ProductGrouping] Availability check failed for variant",
            candidate.id,
            error
          );
        }
      }
    }

    return null;
  }

  /**
   * Switch to a specific variant (programmatically)
   */
  _switchToVariant(variant) {
    if (!variant || !variant.button) {
      console.error('Invalid variant data for switching:', variant);
      return;
    }

    // Simulate a click on the variant button to trigger the normal flow
    const clickEvent = new Event('click', { bubbles: true, cancelable: true });
    variant.button.dispatchEvent(clickEvent);
  }

  /**
   * Helper method to update button state after color change
   */
  _updateButtonStateAfterColorChange(
    addToCartForm,
    soldOutElement,
    isAvailable,
    variant
  ) {
    if (this.debug) {
      console.log(
        "[ProductGrouping] _updateButtonStateAfterColorChange called:",
        {
          hasForm: !!addToCartForm,
          isAvailable,
          variantId: variant?.id,
          variantAvailable: variant?.available
        }
      );
    }
    
    if (!addToCartForm) {
      if (this.debug) {
        console.warn("[ProductGrouping] No addToCartForm found");
      }
      return;
    }

    const buyButtonsEl = addToCartForm.querySelector("buy-buttons");
    const allButtons = buyButtonsEl
      ? Array.from(buyButtonsEl.querySelectorAll("button"))
      : [];

    const notifyMeBtn = allButtons.find((btn) =>
      btn.hasAttribute("data-bis-trigger")
    );
    const addToCartButton = this._buyButtonsSubmitButton(allButtons);
    const addToCartButtonText =
      addToCartButton?.querySelector("div") || addToCartButton;

    if (this.debug) {
      console.log(
        "[ProductGrouping] Found buttons:",
        {
          hasAddToCartButton: !!addToCartButton,
          hasNotifyMeBtn: !!notifyMeBtn,
          hasButtonText: !!addToCartButtonText,
          totalButtons: allButtons.length
        }
      );
    }

    if (addToCartButton && notifyMeBtn) {
      // Price will be read directly from sale-price element in _updateButtonVisibility
      // No need to calculate it here - just pass variant for availability check
      const variantForPrice = {
        id: variant.id,
        price: typeof variant.price === 'string'
          ? parseInt(variant.price, 10)
          : variant.price
      };

      // When color changes, the HTML is replaced, so we need to wait for the new price-list to be ready
      // Use requestAnimationFrame to ensure DOM is updated, then try multiple times if needed
      const updateButton = () => {
        this._updateButtonVisibility(
          addToCartButton,
          notifyMeBtn,
          addToCartButtonText,
          soldOutElement,
          isAvailable,
          variantForPrice
        );
        
        // Check if button text was updated with a price
        const buttonText = addToCartButtonText?.textContent || '';
        const hasPrice = buttonText.includes('$') && /\$\d/.test(buttonText);
        
        // If no price in button text, retry after a short delay (price-list might still be updating)
        if (!hasPrice && isAvailable) {
          setTimeout(() => {
            this._updateButtonVisibility(
              addToCartButton,
              notifyMeBtn,
              addToCartButtonText,
              soldOutElement,
              isAvailable,
              variantForPrice
            );
          }, 150);
        }
      };
      
      // Wait for DOM to update after color change (double RAF to ensure all updates are done)
      requestAnimationFrame(() => {
        requestAnimationFrame(updateButton);
      });

      // Update quick add CTA
      if (variant.price) {
        const priceInCents = typeof variant.price === 'string'
          ? parseInt(variant.price, 10)
          : variant.price;
        if (!Number.isNaN(priceInCents) && priceInCents > 0) {
          const dollars = priceInCents / 100;
          const variantPriceForCTA = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
          this.updateQuickAddCTA(isAvailable, variant.id.toString(), variantPriceForCTA);
        }
      }

      // Notify AMP Back in Stock app
      this._notifyAmpBackInStock({
        id: variant.id.toString(),
        available: isAvailable,
        price: variant.price,
      });

      // Dispatch variant:change event
      document.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            variant: {
              id: variant.id.toString(),
              available: isAvailable,
              price: variant.price,
            },
            formId: addToCartForm?.id,
          },
        })
      );
    }
  }

  /**
   * CRITICAL SAFETY NET: Monitor button state and prevent unwanted hiding
   * This acts as a final safeguard against any code that might hide the Add to Cart button
   * OPTIMIZED: Uses MutationObserver instead of polling for better performance
   */
  _setupButtonStateGuardian() {
    // Wait for DOM to be fully ready
    setTimeout(() => {
      const addToCartForm = document.querySelector(".product-info .shopify-product-form");
      if (!addToCartForm) return;

      const buyButtonsEl = addToCartForm.querySelector("buy-buttons");
      if (!buyButtonsEl) return;

      let isVerifying = false; // Prevent concurrent verifications

      const verifyButtonState = () => {
        // Skip if already verifying or no variant selected
        if (isVerifying || !this.currentVariantId) return;

        const allButtons = Array.from(buyButtonsEl.querySelectorAll("button"));
        const notifyMeBtn = allButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const addToCartButton = this._buyButtonsSubmitButton(allButtons);

        if (!addToCartButton || !notifyMeBtn) return;

        // Check for suspicious button states
        const isAddToCartHidden =
          addToCartButton.style.display === "none" ||
          addToCartButton.style.visibility === "hidden";
        const isWaitlistHidden = notifyMeBtn.style.display === "none";

        // If both are hidden, that's definitely wrong
        if (isAddToCartHidden && isWaitlistHidden) {
          if (this.debug) {
            console.warn(
              "[ProductGrouping] Both buttons hidden - correcting state"
            );
          }

          isVerifying = true;
          const guardianVariantId = this.currentVariantId;
          this._fetchVariantAvailability(guardianVariantId)
            .then((isAvailable) => {
              if (String(this.currentVariantId) !== String(guardianVariantId)) {
                return;
              }
              const soldOutElement = document.querySelector(
                ".product-info sold-out-badge"
              );
              const buttonText = addToCartButton.querySelector("div");

              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                buttonText,
                soldOutElement,
                isAvailable
              );

              if (this.debug) {
                console.log(
                  `[ProductGrouping] Button state corrected to: ${
                    isAvailable ? "Add to Bag" : "Waitlist"
                  }`
                );
              }
            })
            .catch((error) => {
              console.error(
                "[ProductGrouping] Guardian failed to verify availability:",
                error
              );
            })
            .finally(() => {
              isVerifying = false;
            });
        }
      };

      // Use MutationObserver to watch for style changes (much more efficient than polling)
      const observer = new MutationObserver((mutations) => {
        // Only verify if style attributes changed
        const styleChanged = mutations.some(
          (m) => m.type === "attributes" && m.attributeName === "style"
        );
        if (styleChanged) {
          verifyButtonState();
        }
      });

      // Observe both buttons for style changes
      const allButtons = Array.from(buyButtonsEl.querySelectorAll("button"));
      allButtons.forEach((btn) => {
        observer.observe(btn, { attributes: true, attributeFilter: ["style"] });
      });

      // Initial check
      verifyButtonState();

      // Store cleanup function
      this._guardianCleanup = () => {
        observer.disconnect();
      };
    }, 100);
  }
}

customElements.define('product-grouping', ProductGrouping);

// --- faherty-size-guide (inlined: avoids a separate module fetch failing silently) ---

/**
 * Size guide modal (open/close, main tabs, table sub-tabs / "slider")
 * from `snippets/size-chart.liquid`.
 *
 * Uses **event delegation** on `document` so the Size Guide still works when
 * `product-grouping-tag-data` replaces `#main-product-info` after the module loads
 * (a one-time `querySelector('.open-size-guide')` bound to a detached/placeholder node
 * is why clicks were silent, including on style-group products like Fireside Fleece).
 */

let delegationBound = false;

/**
 * The modal and button are in the same branch as `size-chart` (e.g. under
 * `product-grouping` or `#main-product-info`); never rely on a global first
 * `document.querySelector` after a section replace.
 */
function findSizeGuideModalForButton(button) {
  if (!button) return null;
  const inHeader = button.closest(".product-grouping__option__sizes-header");
  if (inHeader) {
    const m = inHeader.querySelector(".size-guide-modal");
    if (m) return m;
  }
  let n = button.nextElementSibling;
  for (let i = 0; i < 5 && n; i++) {
    if (n.classList?.contains("size-guide-modal")) return n;
    n = n.nextElementSibling;
  }
  const roots = [
    "product-grouping",
    "#main-product-info",
    "quick-view-modal",
    "product-info",
    ".shopify-section--main-product",
  ];
  for (const sel of roots) {
    const root = button.closest(sel);
    if (root) {
      const m = root.querySelector(".size-guide-modal");
      if (m) return m;
    }
  }
  return (
    document.querySelector("#main-product-info .size-guide-modal") ||
    document.querySelector("quick-view-modal .size-guide-modal") ||
    document.querySelector(".size-guide-modal")
  );
}

function getActiveSizeGuideModal() {
  return document.querySelector(".size-guide-modal.active");
}

function openSizeGuideModalForEl(modal) {
  if (!modal) return;
  modal.classList.add("active");
  document.body.classList.add("modal-open");
  generateSizeChartSubTabsInModal(modal);
}

function closeSizeGuideModalForEl(modal) {
  (modal || getActiveSizeGuideModal())?.classList.remove("active");
  document.body.classList.remove("modal-open");
}

function switchSizeGuideTabInModal(event, tabButton) {
  const selectedTab = tabButton.getAttribute("data-tab");
  if (!selectedTab) return;
  const modal = tabButton.closest(".size-guide-modal");
  if (!modal) return;
  modal
    .querySelectorAll(".size-guide-tab-buttons .tab-button")
    .forEach((btn) => btn.classList.remove("active"));
  modal
    .querySelectorAll(".size-guide-tab-content .tab-panel")
    .forEach((panel) => panel.classList.remove("active"));
  tabButton.classList.add("active");
  const panel = document.getElementById(selectedTab);
  if (panel && modal.contains(panel)) panel.classList.add("active");
}

function switchSubTab(_event, subButton) {
  const tab = subButton.getAttribute("data-tab");
  if (!tab) return;
  const container = subButton.closest("#size-chart-content");
  if (!container) return;
  container
    .querySelectorAll(".sub-tab-button")
    .forEach((btn) => btn.classList.remove("active"));
  container
    .querySelectorAll(".sub-tab-panel")
    .forEach((pnl) => pnl.classList.remove("active"));
  subButton.classList.add("active");
  const panel = document.getElementById(tab);
  if (panel && container.contains(panel)) {
    panel.classList.add("active");
  }
}

function generateSizeChartSubTabsInModal(modal) {
  if (!modal) return;
  const sizeChartContent = modal.querySelector("#size-chart-content");
  if (!sizeChartContent) return;

  const tables = sizeChartContent.querySelectorAll("table");
  const paragraphs = sizeChartContent.querySelectorAll("p");

  if (tables.length === 0) return;

  let subTabsHTML = `<div class="size-guide-sub-tabs" role="tablist">`;
  let subContentHTML = `<div class="size-guide-sub-content">`;
  let hasTabs = false;

  tables.forEach((table, index) => {
    let label = null;
    for (let i = paragraphs.length - 1; i >= 0; i--) {
      if (
        paragraphs[i].nextElementSibling === table &&
        paragraphs[i].textContent.trim() !== ""
      ) {
        label = paragraphs[i].textContent.trim();
        break;
      }
    }
    if (label) {
      hasTabs = true;
      subTabsHTML += `<button class="sub-tab-button ${
        index === 0 ? "active" : ""
      }" data-tab="sub-tab-${index}" role="tab" aria-controls="sub-tab-${index}" aria-selected="${
        index === 0 ? "true" : "false"
      }">${label}</button>`;
    }
    subContentHTML += `<div class="sub-tab-panel ${
      index === 0 ? "active" : ""
    }" id="sub-tab-${index}" role="tabpanel">${table.outerHTML}</div>`;
  });

  subTabsHTML += `</div>`;
  subContentHTML += `</div>`;

  sizeChartContent.innerHTML = hasTabs
    ? subTabsHTML + subContentHTML
    : subContentHTML;
}

function onEscapeKey(event) {
  if (event.key !== "Escape") return;
  const active = getActiveSizeGuideModal();
  if (active) {
    event.preventDefault();
    closeSizeGuideModalForEl(active);
  }
}

/**
 * document-level: works for any `.open-size-guide` injected after load
 * (e.g. `product-grouping-tag-data` replace, style-group, or variant picker + tag).
 */
function onDocumentClick(event) {
  const openBtn = event.target?.closest?.(".open-size-guide");
  if (openBtn) {
    event.preventDefault();
    const modal = findSizeGuideModalForButton(openBtn);
    if (modal) {
      openSizeGuideModalForEl(modal);
    }
    return;
  }

  const subBtn = event.target?.closest?.(".sub-tab-button");
  if (subBtn) {
    event.preventDefault();
    switchSubTab(event, subBtn);
    return;
  }

  const mainTab = event.target?.closest?.(
    ".size-guide-tab-buttons .tab-button"
  );
  if (mainTab) {
    event.preventDefault();
    switchSizeGuideTabInModal(event, mainTab);
    return;
  }

  const closeBtn = event.target?.closest?.(".size-guide-modal .modal-close");
  if (closeBtn) {
    event.preventDefault();
    const modal = closeBtn.closest(".size-guide-modal");
    closeSizeGuideModalForEl(modal);
    return;
  }

  // Close when clicking anywhere inside `.size-guide-modal` that is not within
  // `.modal-content` (covers both the `.modal-overlay` sibling and the empty
  // flex-centered area around the content panel).
  const modalEl = event.target?.closest?.(".size-guide-modal.active");
  if (modalEl && !event.target.closest(".size-guide-modal .modal-content")) {
    closeSizeGuideModalForEl(modalEl);
  }
}

let escapeBound = false;

/**
 * Binds one delegated listener; does not require `.open-size-guide` to exist yet.
 */
function initFahertySizeGuide() {
  if (delegationBound) return;
  delegationBound = true;
  document.addEventListener("click", onDocumentClick, false);
  if (!escapeBound) {
    escapeBound = true;
    document.addEventListener("keydown", onEscapeKey, false);
  }
}

initFahertySizeGuide();
